// [PATCH:empresa_dados_v1] nome da empresa: Elo Gestao de Residuos
// src/App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Truck, Users, Car, Package, MapPin, AlertTriangle, Plus, Trash2,
  Home, Phone, Mail, Hash, RefreshCw, X, Menu, ChevronDown,
  Clock, Navigation, FileText, User, Settings, Eye, ArrowRight, ArrowLeft,
  CheckCircle, Camera, Download, Check, DollarSign, Search, Calendar, Edit3,
  BarChart2, LogOut, Lock
} from 'lucide-react';

// ==========================================
// API + AUTH
// ==========================================
const API = '/api';

let authToken = null;

const setAuthToken = (token) => {
  authToken = token;
};

const getAuthHeaders = (extra = {}) => {
  const headers = { ...extra };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
};

const fetchJSON = async (url, options = {}) => {
  const headers = getAuthHeaders({ 'Content-Type': 'application/json', ...(options.headers || {}) });
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    setAuthToken(null);
    window.location.reload();
    return {};
  }
  return res.json();
};

const authedFetch = async (url, options = {}) => {
  const headers = getAuthHeaders(options.headers || {});
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    setAuthToken(null);
    window.location.reload();
    return res;
  }
  return res;
};

const pdfUrl = (path) => {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}token=${authToken}`;
};

// ========== HELPERS DE TIPO ==========
const sortByNumero = (list) => [...list].sort((a, b) => (parseInt(a.numero) || 0) - (parseInt(b.numero) || 0));

const tipoPrecisaCtr = (tipo) => tipo === 'recolher' || tipo === 'trocar' || tipo === 'recolher_entregar';
const tipoTemCacambaEntregar = (tipo) => tipo === 'trocar' || tipo === 'recolher_entregar';
const tipoRecolheDoCLiente = (tipo) => tipo === 'recolher' || tipo === 'trocar' || tipo === 'recolher_entregar';

const tipoBadge = (tipo) => {
  const map = {
    entregar: { text: '\u2193 Entregar', color: 'blue' },
    recolher: { text: '\u2191 Recolher', color: 'green' },
    trocar: { text: '\u2194 Trocar', color: 'purple' },
    recolher_entregar: { text: '\u2191\u2193 Rec+Ent', color: 'orange' },
  };
  return map[tipo] || { text: tipo, color: 'gray' };
};

// ========== SUB COMPONENTS ==========

function Stat({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    teal: 'bg-teal-50 text-teal-600 border-teal-200',
  };
  return (
    <div className={`rounded-xl border-2 p-5 ${colors[color] || colors.blue}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-70">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <Icon size={36} className="opacity-40" />
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function BigModal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FullModal({ title, onClose, children, actions }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <div className="flex items-center gap-2">
            {actions}
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input {...props} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select {...props} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white">
        {children}
      </select>
    </div>
  );
}

function Badge({ text, color }) {
  const c = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-100 text-gray-700',
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
    teal: 'bg-teal-100 text-teal-700',
  };
  return <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c[color] || c.gray}`}>{text}</span>;
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="text-center py-12 text-gray-400">
      <Icon size={48} className="mx-auto mb-3 opacity-50" />
      <p>{message}</p>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  React.useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  const bg = type === 'error' ? 'bg-red-500' : 'bg-green-500';
  return (
    <div className={`fixed top-4 right-4 z-[60] ${bg} text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2`}>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose}><X size={16} /></button>
    </div>
  );
}

// ========== LOGIN PAGE ==========

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        onLogin(data.token, data.username);
      } else {
        setError(data.error || 'Credenciais invalidas');
      }
    } catch (err) {
      setError('Erro de conexao com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-8 text-center">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Elo Gestao de Residuos</h1>
          <p className="text-blue-200 text-sm mt-1">Sistema de Gestao de Cacambas</p>
        </div>
        <form onSubmit={handleSubmit} className="p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="admin"
                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                autoFocus
                required
              />
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="********"
                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <LogOut size={16} />}
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ========== HELPER: botao "+" para cadastro rapido ==========
function AddBtn({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || 'Cadastrar novo'}
      className="flex-shrink-0 w-9 h-[34px] bg-green-50 border border-green-300 text-green-700 rounded-lg hover:bg-green-100 hover:border-green-500 transition flex items-center justify-center"
    >
      <Plus size={14} />
    </button>
  );
}

// Wrapper que combina <Select> + AddBtn em linha
function SelectWithAdd({ label, value, onChange, onAdd, addTitle, children }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-1.5">
        <select
          value={value}
          onChange={onChange}
          className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
        >
          {children}
        </select>
        <AddBtn onClick={onAdd} title={addTitle} />
      </div>
    </div>
  );
}

// ========== HELPER: render operacao label ==========
function OpLabel({ op }) {
  if (op.tipo === 'trocar') {
    return (
      <span className="flex items-center gap-1 flex-wrap">
        <span className="text-gray-700">{'\u2191'} #{op.cacamba_numero || op.cacamba_id} ({op.cacamba_tamanho || ''})</span>
        <ArrowRight size={12} className="text-gray-400" />
        {op.cacamba_entregar_numero ? (
          <span className="text-blue-700">{'\u2193'} #{op.cacamba_entregar_numero} ({op.cacamba_entregar_tamanho || ''})</span>
        ) : (
          <span className="text-gray-400">sem cacamba entrega</span>
        )}
      </span>
    );
  }
  if (op.tipo === 'recolher_entregar') {
    return (
      <span className="flex items-center gap-1 flex-wrap">
        <span className="text-gray-700">{'\u2191'} #{op.cacamba_numero || op.cacamba_id} ({op.cacamba_tamanho || ''})</span>
        <ArrowRight size={12} className="text-gray-400" />
        {op.cacamba_entregar_numero ? (
          <span className="text-orange-700">{'\u2193'} #{op.cacamba_entregar_numero} ({op.cacamba_entregar_tamanho || ''})</span>
        ) : (
          <span className="text-gray-400">sem cacamba entrega</span>
        )}
        {op.cliente_entregar_nome && (
          <>
            <ArrowRight size={12} className="text-orange-400" />
            <span className="text-orange-700 font-medium">{'\u2192'} {op.cliente_entregar_nome}</span>
          </>
        )}
      </span>
    );
  }
  return (
    <span className="text-gray-700">#{op.cacamba_numero || op.cacamba_id} ({op.cacamba_tamanho || ''})</span>
  );
}

// ========== MINI-MODAL: cadastro rapido de Cliente ou Cacamba ==========
function QuickAddClienteModal({ onClose, onCreated, showToast }) {
  const [f, setF] = React.useState({ nome: '', cpf_cnpj: '', endereco: '', telefone: '', email: '' });
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (!f.nome || !f.cpf_cnpj || !f.endereco || !f.telefone) {
      showToast('Preencha nome, CPF/CNPJ, endereco e telefone', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(API + '/clientes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(f)
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        showToast(data.error || 'Erro ao cadastrar cliente', 'error');
        return;
      }
      showToast('Cliente cadastrado e selecionado');
      onCreated(data.id);
      onClose();
    } catch {
      showToast('Erro ao cadastrar cliente', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2"><Plus size={16} className="text-green-600" /> Cadastro rapido de Cliente</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-4">
          <Input label="Nome *" value={f.nome} onChange={e => setF({...f, nome: e.target.value})} placeholder="Nome completo" autoFocus />
          <Input label="CPF/CNPJ *" value={f.cpf_cnpj} onChange={e => setF({...f, cpf_cnpj: e.target.value})} placeholder="000.000.000-00" />
          <Input label="Endereco *" value={f.endereco} onChange={e => setF({...f, endereco: e.target.value})} placeholder="Rua, numero, bairro" />
          <Input label="Telefone *" value={f.telefone} onChange={e => setF({...f, telefone: e.target.value})} placeholder="(00) 00000-0000" />
          <Input label="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="email@exemplo.com" />
          <div className="flex gap-2 mt-3">
            <button onClick={onClose} disabled={saving} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 text-sm">Cancelar</button>
            <button onClick={submit} disabled={saving} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? 'Salvando...' : 'Cadastrar e usar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAddCacambaModal({ onClose, onCreated, showToast, contexto, clientes }) {
  const ctxLoc = (contexto && contexto.localizacao) || 'base';
  const ctxCli = contexto && contexto.cliente_id ? String(contexto.cliente_id) : '';
  const [f, setF] = React.useState({ numero: '', tamanho: '', localizacao: ctxLoc, cliente_id: ctxCli });
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (!f.numero || !f.tamanho) {
      showToast('Numero e tamanho sao obrigatorios', 'error');
      return;
    }
    if (f.localizacao === 'cliente' && !f.cliente_id) {
      showToast('Selecione o cliente onde a cacamba ja esta', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { numero: f.numero, tamanho: f.tamanho, localizacao: f.localizacao, cliente_id: f.localizacao === 'cliente' ? parseInt(f.cliente_id) : null };
      const res = await fetch(API + '/cacambas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        showToast(data.error || 'Erro ao cadastrar cacamba', 'error');
        return;
      }
      showToast('Cacamba cadastrada e selecionada');
      onCreated(data.id);
      onClose();
    } catch {
      showToast('Erro ao cadastrar cacamba', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2"><Plus size={16} className="text-green-600" /> Cadastro rapido de Cacamba</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-4">
          <Input label="Numero *" value={f.numero} onChange={e => setF({...f, numero: e.target.value})} placeholder="Ex: 042" autoFocus />
          <Select label="Tamanho *" value={f.tamanho} onChange={e => setF({...f, tamanho: e.target.value})}>
            <option value="">Selecione...</option>
            <option value="3m3">3 m3</option>
            <option value="5m3">5 m3</option>
            <option value="7m3">7 m3</option>
            <option value="10m3">10 m3</option>
          </Select>
          <Select label="Localizacao inicial" value={f.localizacao} onChange={e => setF({...f, localizacao: e.target.value})}>
            <option value="base">Na base</option>
            <option value="cliente">Em um cliente</option>
          </Select>
          {f.localizacao === 'cliente' && (
            <Select label="Cliente *" value={f.cliente_id} onChange={e => setF({...f, cliente_id: e.target.value})}>
              <option value="">Selecione...</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          )}
          {ctxLoc === 'cliente' && (
            <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">
              Esta operacao espera uma cacamba ja no cliente. Sugerimos manter localizacao "Em um cliente".
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={onClose} disabled={saving} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 text-sm">Cancelar</button>
            <button onClick={submit} disabled={saving} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? 'Salvando...' : 'Cadastrar e usar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== ROTA FORM (top-level para evitar recreacao a cada render) ==========
// [PATCH:rotaform_toplevel]
function RotaFormComponent({ clientes, motoristas, veiculos, cacambas, ctrs,
  onSubmit, onClose, setQuickAdd }) {

  const [f, setF] = React.useState({
    nome: '', data_hora: '', motorista_id: '', veiculo_id: '', observacoes: ''
  });
  const [ops, setOps] = React.useState([{
    tipo: 'entregar', cacamba_id: '', cacamba_entregar_id: '',
    cliente_id: '', cliente_entregar_id: '', cliente_nome_auto: '', observacoes: ''
  }]);

  const cacambasNaBase    = cacambas.filter(c => c.localizacao_atual === 'base');
  const cacambasNoCliente = cacambas.filter(c => c.localizacao_atual === 'cliente');

  const addOp = () => setOps(prev => [...prev, {
    tipo: 'entregar', cacamba_id: '', cacamba_entregar_id: '',
    cliente_id: '', cliente_entregar_id: '', cliente_nome_auto: '', observacoes: ''
  }]);
  const removeOp = (i) => setOps(prev => prev.filter((_, idx) => idx !== i));

  const updateOp = (i, field, val) => {
    setOps(prev => {
      const n = prev.map((op, idx) => idx === i ? { ...op, [field]: val } : op);
      if (field === 'tipo') {
        n[i] = { ...n[i], cacamba_id: '', cacamba_entregar_id: '',
          cliente_id: '', cliente_entregar_id: '', cliente_nome_auto: '' };
      }
      if (field === 'cacamba_id' && tipoRecolheDoCLiente(n[i].tipo) && val) {
        const cab = cacambas.find(c => c.id === parseInt(val));
        if (cab && cab.cliente_id) {
          n[i] = { ...n[i], cliente_id: String(cab.cliente_id), cliente_nome_auto: cab.cliente_nome || '' };
        } else {
          n[i] = { ...n[i], cliente_id: '', cliente_nome_auto: '' };
        }
      }
      return n;
    });
  };

  const submit = () => {
    if (!f.veiculo_id) { onSubmit(null, 'Selecione um veiculo para a rota'); return; }
    if (!f.data_hora)  { onSubmit(null, 'Informe a data e hora da rota'); return; }
    const payload = {
      ...f,
      motorista_id: f.motorista_id || null,
      veiculo_id:   f.veiculo_id   || null,
      operacoes: ops.map(o => ({
        tipo: o.tipo,
        cacamba_id: parseInt(o.cacamba_id),
        cacamba_entregar_id: tipoTemCacambaEntregar(o.tipo) && o.cacamba_entregar_id
          ? parseInt(o.cacamba_entregar_id) : null,
        cliente_id: o.cliente_id ? parseInt(o.cliente_id) : null,
        cliente_entregar_id: o.tipo === 'recolher_entregar' && o.cliente_entregar_id
          ? parseInt(o.cliente_entregar_id) : null,
        observacoes: o.observacoes || null
      }))
    };
    onSubmit(payload);
  };

  return (
    <BigModal title="Nova Rota" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Nome da Rota" value={f.nome}
          onChange={e => setF(prev => ({ ...prev, nome: e.target.value }))}
          placeholder="Ex: Rota manha" />
        <Input label="Data/Hora *" type="datetime-local" value={f.data_hora}
          onChange={e => setF(prev => ({ ...prev, data_hora: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Motorista" value={f.motorista_id}
          onChange={e => setF(prev => ({ ...prev, motorista_id: e.target.value }))}>
          <option value="">Selecione...</option>
          {motoristas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
        </Select>
        <Select label="Veiculo *" value={f.veiculo_id}
          onChange={e => setF(prev => ({ ...prev, veiculo_id: e.target.value }))}>
          <option value="">Selecione...</option>
          {veiculos.map(v => <option key={v.id} value={v.id}>{v.placa} - {v.modelo}</option>)}
        </Select>
      </div>
      <Input label="Observacoes" value={f.observacoes}
        onChange={e => setF(prev => ({ ...prev, observacoes: e.target.value }))}
        placeholder="Observacoes opcionais" />

      <div className="mt-4 mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700">Operacoes</h3>
        <button onClick={addOp}
          className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium hover:bg-green-200 flex items-center gap-1">
          <Plus size={14} /> Adicionar
        </button>
      </div>

      {ops.map((op, i) => {
        const isTrocar   = op.tipo === 'trocar';
        const isRecolher = op.tipo === 'recolher';
        const isEntregar = op.tipo === 'entregar';
        const isRecEnt   = op.tipo === 'recolher_entregar';

        const openAddCliente = (slot) => setQuickAdd && setQuickAdd({
          tipo: 'cliente', opIndex: i, slot,
          onCreated: (novoId) => updateOp(i, slot, String(novoId))
        });
        const openAddCacamba = (slot) => setQuickAdd && setQuickAdd({
          tipo: 'cacamba', opIndex: i, slot,
          contexto: {
            localizacao:
              (slot === 'cacamba_entregar_id' && op.tipo === 'recolher_entregar') ? 'cliente' :
              (slot === 'cacamba_id' && tipoRecolheDoCLiente(op.tipo)) ? 'cliente' : 'base',
            cliente_id: op.cliente_id || null
          },
          onCreated: (novoId) => updateOp(i, slot, String(novoId))
        });

        return (
          <div key={i} className="border rounded-lg p-3 mb-2 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-500">Operacao {i + 1}</span>
              {ops.length > 1 && (
                <button onClick={() => removeOp(i)} className="text-red-400 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select label="Tipo" value={op.tipo} onChange={e => updateOp(i, 'tipo', e.target.value)}>
                <option value="entregar">Entregar</option>
                <option value="recolher">Recolher</option>
                <option value="trocar">Trocar</option>
                <option value="recolher_entregar">Recolha + Entrega</option>
              </Select>
              {isEntregar && (<>
                <SelectWithAdd label="Cacamba (na base)" value={op.cacamba_id}
                  onChange={e => updateOp(i, 'cacamba_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNaBase.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho})</option>)}
                </SelectWithAdd>
                <SelectWithAdd label="Cliente" value={op.cliente_id}
                  onChange={e => updateOp(i, 'cliente_id', e.target.value)}
                  onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                  <option value="">Selecione...</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </SelectWithAdd>
              </>)}
              {isRecolher && (<>
                <SelectWithAdd label="Cacamba (no cliente)" value={op.cacamba_id}
                  onChange={e => updateOp(i, 'cacamba_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNoCliente.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                </SelectWithAdd>
                {op.cliente_nome_auto ? (
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                    <div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-2 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div>
                  </div>
                ) : (
                  <SelectWithAdd label="Cliente" value={op.cliente_id}
                    onChange={e => updateOp(i, 'cliente_id', e.target.value)}
                    onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                    <option value="">Selecione...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </SelectWithAdd>
                )}
              </>)}
              {isTrocar && (<>
                <SelectWithAdd label={'↑ Recolher (no cliente)'} value={op.cacamba_id}
                  onChange={e => updateOp(i, 'cacamba_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNoCliente.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                </SelectWithAdd>
                <SelectWithAdd label={'↓ Entregar (da base)'} value={op.cacamba_entregar_id}
                  onChange={e => updateOp(i, 'cacamba_entregar_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_entregar_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNaBase.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho})</option>)}
                </SelectWithAdd>
              </>)}
              {isRecEnt && (<>
                <SelectWithAdd label={'↑ Recolher (no cliente)'} value={op.cacamba_id}
                  onChange={e => updateOp(i, 'cacamba_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNoCliente.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                </SelectWithAdd>
                <SelectWithAdd label={'↓ Entregar (do cliente)'} value={op.cacamba_entregar_id}
                  onChange={e => updateOp(i, 'cacamba_entregar_id', e.target.value)}
                  onAdd={() => openAddCacamba('cacamba_entregar_id')} addTitle="Nova cacamba">
                  <option value="">Selecione...</option>
                  {cacambasNoCliente.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                </SelectWithAdd>
              </>)}
            </div>
            {isTrocar && (
              <div className="mt-2">
                {op.cliente_nome_auto ? (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Cliente (auto)</label>
                    <div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-1.5 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div>
                  </div>
                ) : (
                  <SelectWithAdd label="Cliente" value={op.cliente_id}
                    onChange={e => updateOp(i, 'cliente_id', e.target.value)}
                    onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                    <option value="">Selecione...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </SelectWithAdd>
                )}
              </div>
            )}
            {isRecEnt && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {op.cliente_nome_auto ? (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Cliente Recolha (auto)</label>
                    <div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-1.5 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div>
                  </div>
                ) : (
                  <SelectWithAdd label="Cliente Recolha" value={op.cliente_id}
                    onChange={e => updateOp(i, 'cliente_id', e.target.value)}
                    onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                    <option value="">Selecione...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </SelectWithAdd>
                )}
                <SelectWithAdd label="Cliente Entrega *" value={op.cliente_entregar_id || ''}
                  onChange={e => updateOp(i, 'cliente_entregar_id', e.target.value)}
                  onAdd={() => openAddCliente('cliente_entregar_id')} addTitle="Novo cliente">
                  <option value="">Selecione...</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </SelectWithAdd>
              </div>
            )}
            <div className="mt-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Observacao da operacao</label>
              <input type="text" value={op.observacoes}
                onChange={e => updateOp(i, 'observacoes', e.target.value)}
                placeholder="Obs. opcional..."
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            {tipoRecolheDoCLiente(op.tipo) && !op.cacamba_id && (
              <p className="text-xs text-amber-600 mt-1">Selecione a cacamba para preencher o cliente automaticamente</p>
            )}
          </div>
        );
      })}
      <button onClick={submit}
        className="w-full mt-3 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">
        Criar Rota
      </button>
    </BigModal>
  );
}

// ========== MAIN APP ==========

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState('');
  const [page, setPage] = useState('dashboard');
  const [sideOpen, setSideOpen] = useState(true);
  const [toast, setToast] = useState(null);

  const [clientes, setClientes] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [cacambas, setCacambas] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [paradas, setParadas] = useState([]);
  const [ctrs, setCtrs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState(null);

  const [rotaSelecionada, setRotaSelecionada] = useState(null);
  // [PATCH:rotas_layout_v2_hotfix1] estados do RotasPage levantados para o App
  const [rotaFiltro, setRotaFiltro] = useState('todas');
  const [rotaBusca, setRotaBusca] = useState('');
  const [rotasExpandidas, setRotasExpandidas] = useState({});
  // [PATCH:rotas_v3]
  const [grupoPickerOpen, setGrupoPickerOpen] = useState(null);
  // [PATCH:ordem_conclusao_v1_front]
  const [conflitoConclusao, setConflitoConclusao] = useState(null); // [PATCH:ordem_conclusao_v1]

  // [PATCH:quickadd_v1] estado do mini-modal de cadastro rapido
  const [quickAdd, setQuickAdd] = useState(null);
  // formato: { tipo: 'cliente'|'cacamba', opIndex, slot, contexto?, onCreated }
  // slot identifica qual campo da operacao recebe o ID do novo registro:
  //   'cliente_id' | 'cliente_entregar_id' | 'cacamba_id' | 'cacamba_entregar_id'
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [finalizacaoData, setFinalizacaoData] = useState({ observacoes_finalizacao: '', operacoes: [] });
  const [finalizando, setFinalizando] = useState(false);

  const [showEditarRota, setShowEditarRota] = useState(false);
  const [editandoRotaData, setEditandoRotaData] = useState(null);

  const [fatClienteId, setFatClienteId] = useState('');
  const [fatDataInicio, setFatDataInicio] = useState('');
  const [fatDataFim, setFatDataFim] = useState('');
  const [fatOperacoes, setFatOperacoes] = useState([]);
  const [fatSelecionadas, setFatSelecionadas] = useState({});
  const [fatBuscando, setFatBuscando] = useState(false);
  const [fatGerando, setFatGerando] = useState(false);
  const [medicaoTab, setMedicaoTab] = useState('nova');
  const [medicoesHistorico, setMedicoesHistorico] = useState([]);
  const [editandoMedicao, setEditandoMedicao] = useState(null);

  const [relCtrId, setRelCtrId] = useState('');
  const [relDataInicio, setRelDataInicio] = useState('');
  const [relDataFim, setRelDataFim] = useState('');
  const [relResultado, setRelResultado] = useState(null);
  const [relBuscando, setRelBuscando] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // ---- LOGIN / LOGOUT ----
  const handleLogin = (token, username) => {
    setAuthToken(token);
    setIsAuthenticated(true);
    setCurrentUser(username);
  };

  const handleLogout = async () => {
    try { await fetchJSON(`${API}/logout`, { method: 'POST' }); } catch {}
    setAuthToken(null);
    setIsAuthenticated(false);
    setCurrentUser('');
  };

  // ---- COMPUTED ----
  const rotasOrdenadas = useMemo(() => {
    return [...rotas].sort((a, b) => new Date(b.data_hora || 0) - new Date(a.data_hora || 0));
  }, [rotas]);

  // ---- LOAD ----
  const load = useCallback(async (entity) => {
    try {
      const data = await fetchJSON(`${API}/${entity}`);
      if (Array.isArray(data)) {
        const setters = {
          clientes: setClientes, motoristas: setMotoristas, veiculos: setVeiculos,
          cacambas: (d) => setCacambas(sortByNumero(d)),
          rotas: setRotas, ctrs: setCtrs
        };
        if (entity === 'cacambas') {
          setCacambas(sortByNumero(data));
        } else {
          const setter = { clientes: setClientes, motoristas: setMotoristas, veiculos: setVeiculos, rotas: setRotas, ctrs: setCtrs }[entity];
          if (setter) setter(data);
        }
      }
    } catch {}
  }, []);

  const loadParadas = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API}/cacambas/paradas`);
      if (Array.isArray(data)) setParadas(sortByNumero(data));
    } catch {}
  }, []);

  const loadMedicoesHistorico = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API}/medicoes`);
      if (Array.isArray(data)) setMedicoesHistorico(data);
    } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    await Promise.all([
      load('clientes'), load('motoristas'), load('veiculos'),
      load('cacambas'), load('rotas'), load('ctrs'), loadParadas(),
      loadMedicoesHistorico()
    ]);
    setLoading(false);
  }, [load, loadParadas, loadMedicoesHistorico, isAuthenticated]);

  useEffect(() => { if (isAuthenticated) loadAll(); }, [loadAll, isAuthenticated]);

  // ---- CRUD ----
  const handleDelete = async (entity, id, label) => {
    if (!window.confirm(`Deseja realmente deletar este ${label}?`)) return;
    try {
      const res = await fetchJSON(`${API}/${entity}/${id}`, { method: 'DELETE' });
      if (res && res.error) { showToast(res.error, 'error'); return; }
      showToast(`${label} deletado com sucesso`);
      load(entity);
      if (entity === 'cacambas') loadParadas();
    } catch {
      showToast('Erro ao deletar', 'error');
    }
  };

  const handleCreate = async (entity, data, label) => {
    try {
      const res = await fetchJSON(`${API}/${entity}`, { method: 'POST', body: JSON.stringify(data) });
      if (res.error) { showToast(res.error, 'error'); return; }
      showToast(`${label} criado com sucesso`);
      setModal(null);
      load(entity);
      if (entity === 'rotas') { load('cacambas'); loadParadas(); }
    } catch {
      showToast('Erro ao criar', 'error');
    }
  };

  // ---- ROTAS: Detalhes ----
  const abrirDetalhes = async (rota) => {
    try {
      const data = await fetchJSON(`${API}/rotas/${rota.id}`);
      setRotaSelecionada(data);
      setShowDetalhes(true);
    } catch {
      showToast('Erro ao carregar detalhes', 'error');
    }
  };

  const abrirEditarRota = async (rota) => {
    try {
      const data = await fetchJSON(`${API}/rotas/${rota.id}`);
      setEditandoRotaData(data);
      setShowEditarRota(true);
    } catch {
      showToast('Erro ao carregar rota para edicao', 'error');
    }
  };

  const abrirFinalizar = async (rota) => {
    try {
      const data = await fetchJSON(`${API}/rotas/${rota.id}`);
      setRotaSelecionada(data);
      setFinalizacaoData({
        observacoes_finalizacao: '',
        operacoes: (data.operacoes || []).map(op => ({
          ...op,
          concluida: true,
          observacoes_op: op.observacoes || '',
          ctr_id: op.ctr_id || '',
          fotos_novas: [],
          fotos_preview: [],
          fotos_existentes: op.fotos || []
        }))
      });
      setShowFinalizar(true);
    } catch {
      showToast('Erro ao carregar rota', 'error');
    }
  };

  const handleFotoChange = (opIndex, e) => {
    const files = Array.from(e.target.files);
    const ops = [...finalizacaoData.operacoes];
    const previews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      nome: file.name
    }));
    ops[opIndex].fotos_novas = [...ops[opIndex].fotos_novas, ...previews];
    setFinalizacaoData({ ...finalizacaoData, operacoes: ops });
  };

  const removerFotoNova = (opIndex, fotoIndex) => {
    const ops = [...finalizacaoData.operacoes];
    URL.revokeObjectURL(ops[opIndex].fotos_novas[fotoIndex].preview);
    ops[opIndex].fotos_novas.splice(fotoIndex, 1);
    setFinalizacaoData({ ...finalizacaoData, operacoes: ops });
  };

  const toggleConcluida = (opIndex) => {
    const ops = [...finalizacaoData.operacoes];
    ops[opIndex].concluida = !ops[opIndex].concluida;
    setFinalizacaoData({ ...finalizacaoData, operacoes: ops });
  };

  const handleFinalizarRota = async () => {
    const semFoto = finalizacaoData.operacoes.filter(
      op => (op.fotos_existentes?.length || 0) === 0 && (op.fotos_novas?.length || 0) === 0
    );
    if (semFoto.length > 0) {
      showToast('Todas as operacoes precisam de pelo menos uma foto!', 'error');
      return;
    }
    const recolherSemCtr = finalizacaoData.operacoes.filter(
      op => tipoPrecisaCtr(op.tipo) && !op.ctr_id
    );
    if (recolherSemCtr.length > 0) {
      showToast('Selecione o CTR para todas as operacoes de recolha/troca/rec+ent!', 'error');
      return;
    }
    const naoConcluidas = finalizacaoData.operacoes.filter(op => !op.concluida);
    if (naoConcluidas.length > 0) {
      if (!window.confirm(`${naoConcluidas.length} operacao(oes) nao foram marcadas como concluidas. Continuar?`)) return;
    }
    try {
      setFinalizando(true);
      // [PATCH:ordem_conclusao_v1_front] helper local para pausar em conflito
      const aguardarDecisaoConflito = (info) => new Promise(resolve => {
        setConflitoConclusao({
          ...info,
          onConfirmar: () => { setConflitoConclusao(null); resolve('confirmar'); },
          onCancelar:  () => { setConflitoConclusao(null); resolve('cancelar'); }
        });
      });

      for (const op of finalizacaoData.operacoes) {
        if (op.concluida) {
          let resOp = await fetchJSON(`${API}/operacoes/${op.id}/concluir`, {
            method: 'PUT',
            body: JSON.stringify({
              observacoes: op.observacoes_op,
              ctr_id: tipoPrecisaCtr(op.tipo) ? (parseInt(op.ctr_id) || null) : null
            })
          });

          // 409 = conflito de ordem — pausar e perguntar ao usuario
          if (resOp && resOp.tipo === 'aviso_ordem') {
            const decisao = await aguardarDecisaoConflito(resOp);
            if (decisao === 'cancelar') {
              showToast('Finalização pausada. Resolva o conflito antes de continuar.', 'error');
              setFinalizando(false);
              return;
            }
            // Usuario confirmou: retentar com flag de forca
            resOp = await fetchJSON(`${API}/operacoes/${op.id}/concluir`, {
              method: 'PUT',
              body: JSON.stringify({
                observacoes: op.observacoes_op,
                ctr_id: tipoPrecisaCtr(op.tipo) ? (parseInt(op.ctr_id) || null) : null,
                confirmar_fora_de_ordem: true
              })
            });
          }
        }
        for (const foto of op.fotos_novas) {
          const fd = new FormData();
          fd.append('foto', foto.file);
          fd.append('descricao', `Evidencia - Op #${op.id}`);
          await authedFetch(`${API}/operacoes/${op.id}/fotos`, { method: 'POST', body: fd });
        }
      }
      await fetchJSON(`${API}/rotas/${rotaSelecionada.id}/finalizar`, {
        method: 'PUT',
        body: JSON.stringify({ observacoes_finalizacao: finalizacaoData.observacoes_finalizacao })
      });
      showToast('Rota finalizada com sucesso!');
      setShowFinalizar(false);
      load('rotas');
      load('cacambas');
      loadParadas();
    } catch {
      showToast('Erro ao finalizar rota', 'error');
    } finally {
      setFinalizando(false);
    }
  };

  const removerOperacaoDaRota = async (opIndex) => {
    const op = finalizacaoData.operacoes[opIndex];
    if (!op || !rotaSelecionada) return;
    if (!window.confirm('Remover esta operacao da rota?')) return;
    try {
      const res = await fetchJSON(`${API}/rotas/${rotaSelecionada.id}/operacoes/${op.id}`, { method: 'DELETE' });
      if (res && res.error) { showToast(res.error, 'error'); return; }
      // Atualiza finalizacaoData removendo a operacao localmente
      const novasOps = finalizacaoData.operacoes.filter((_, i) => i !== opIndex);
      setFinalizacaoData({ ...finalizacaoData, operacoes: novasOps });
      showToast('Operacao removida da rota');
      load('cacambas'); loadParadas();
    } catch {
      showToast('Erro ao remover operacao', 'error');
    }
  };

    const handleGerarPDF = (id) => {
    window.open(pdfUrl(`${API}/rotas/${id}/pdf`), '_blank');
  };

  // ---- MEDICAO ----
  const buscarOperacoesFaturamento = async () => {
    if (!fatClienteId) { showToast('Selecione um cliente', 'error'); return; }
    setFatBuscando(true);
    try {
      let url = `${API}/faturamento/operacoes?cliente_id=${fatClienteId}`;
      if (fatDataInicio) url += `&data_inicio=${fatDataInicio}`;
      if (fatDataFim) url += `&data_fim=${fatDataFim}`;
      const data = await fetchJSON(url);
      if (Array.isArray(data)) {
        setFatOperacoes(data);
        const sel = {};
        data.forEach(op => {
          sel[op.id] = {
            selecionada: false,
            valor_locacao: op.valor_locacao_anterior || '',
            valor_ctr: op.valor_ctr_anterior || ''
          };
        });
        setFatSelecionadas(sel);
      } else {
        setFatOperacoes([]);
        setFatSelecionadas({});
      }
    } catch {
      showToast('Erro ao buscar operacoes', 'error');
      setFatOperacoes([]);
    } finally {
      setFatBuscando(false);
    }
  };

  const fatToggleSelect = (opId) => {
    setFatSelecionadas(prev => ({
      ...prev,
      [opId]: { ...prev[opId], selecionada: !prev[opId]?.selecionada }
    }));
  };

  const fatToggleAll = () => {
    const allSelected = fatOperacoes.length > 0 && fatOperacoes.every(op => fatSelecionadas[op.id]?.selecionada);
    const newSel = { ...fatSelecionadas };
    fatOperacoes.forEach(op => {
      newSel[op.id] = { ...newSel[op.id], selecionada: !allSelected };
    });
    setFatSelecionadas(newSel);
  };

  const fatUpdateValor = (opId, field, val) => {
    setFatSelecionadas(prev => ({
      ...prev,
      [opId]: { ...prev[opId], [field]: val }
    }));
  };

  const fatEditarOperacao = (op) => {
    setFatSelecionadas(prev => ({
      ...prev,
      [op.id]: {
        selecionada: true,
        valor_locacao: op.valor_locacao_anterior || prev[op.id]?.valor_locacao || '',
        valor_ctr: op.valor_ctr_anterior || prev[op.id]?.valor_ctr || ''
      }
    }));
    showToast('Operacao selecionada para edicao', 'success');
  };

  const fatTotals = useMemo(() => {
    return Object.entries(fatSelecionadas)
      .filter(([_, v]) => v.selecionada)
      .reduce((acc, [_, v]) => ({
        count: acc.count + 1,
        locacao: acc.locacao + (parseFloat(v.valor_locacao) || 0),
        ctr: acc.ctr + (parseFloat(v.valor_ctr) || 0),
      }), { count: 0, locacao: 0, ctr: 0 });
  }, [fatSelecionadas]);

  const handleGerarMedicao = async () => {
    const ops = Object.entries(fatSelecionadas)
      .filter(([_, v]) => v.selecionada)
      .map(([id, v]) => ({
        operacao_id: parseInt(id),
        valor_locacao: parseFloat(v.valor_locacao) || 0,
        valor_ctr: parseFloat(v.valor_ctr) || 0
      }));
    if (ops.length === 0) {
      showToast('Selecione pelo menos uma operacao', 'error');
      return;
    }
    const semValor = ops.filter(o => o.valor_locacao === 0 && o.valor_ctr === 0);
    if (semValor.length > 0) {
      if (!window.confirm(`${semValor.length} operacao(oes) estao com valor zerado. Continuar?`)) return;
    }
    try {
      setFatGerando(true);
      const res = await authedFetch(`${API}/faturamento/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cliente_id: parseInt(fatClienteId), operacoes: ops })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao gerar medicao');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cliente = clientes.find(c => c.id === parseInt(fatClienteId));
      a.download = `medicao_${(cliente?.nome || fatClienteId).replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Medicao gerada com sucesso!');
      loadMedicoesHistorico();
      buscarOperacoesFaturamento();
    } catch (err) {
      showToast(err.message || 'Erro ao gerar medicao', 'error');
    } finally {
      setFatGerando(false);
    }
  };

  const handleDeleteMedicao = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta medicao?')) return;
    try {
      await fetchJSON(`${API}/medicoes/${id}`, { method: 'DELETE' });
      showToast('Medicao excluida com sucesso');
      loadMedicoesHistorico();
    } catch {
      showToast('Erro ao excluir', 'error');
    }
  };

  const handleEditMedicao = async (medicao) => {
    setEditandoMedicao({ ...medicao, operacoes: medicao.operacoes || [] });
    try {
      const data = await fetchJSON(`${API}/medicoes/${medicao.id}`);
      if (data && data.id) {
        setEditandoMedicao(data);
      }
    } catch {}
  };

  const handleGerarPDFMedicao = (id) => {
    window.open(pdfUrl(`${API}/medicoes/${id}/pdf`), '_blank');
  };

  // ---- RELATORIOS CTR ----
  const buscarRelatorioCtr = async () => {
    if (!relCtrId || !relDataInicio || !relDataFim) {
      showToast('Preencha todos os campos', 'error');
      return;
    }
    setRelBuscando(true);
    try {
      const url = `${API}/relatorios/ctr/${relCtrId}?data_inicio=${relDataInicio}&data_fim=${relDataFim}`;
      const data = await fetchJSON(url);
      if (data.error) {
        showToast(data.error, 'error');
        setRelResultado(null);
      } else {
        setRelResultado(data);
      }
    } catch {
      showToast('Erro ao buscar relatorio', 'error');
      setRelResultado(null);
    } finally {
      setRelBuscando(false);
    }
  };

  const gerarPdfRelatorioCtr = () => {
    if (!relCtrId || !relDataInicio || !relDataFim) return;
    window.open(
      pdfUrl(`${API}/relatorios/ctr/${relCtrId}/pdf?data_inicio=${relDataInicio}&data_fim=${relDataFim}`),
      '_blank'
    );
  };

  // ---- NAV ----
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'clientes', label: 'Clientes', icon: Users },
    { id: 'motoristas', label: 'Motoristas', icon: User },
    { id: 'veiculos', label: 'Veiculos', icon: Car },
    { id: 'cacambas', label: 'Cacambas', icon: Package },
    { id: 'ctrs', label: 'CTRs', icon: FileText },
    { id: 'rotas', label: 'Rotas', icon: Navigation },
    { id: 'medicao', label: 'Medicao', icon: DollarSign },
    { id: 'relatorios', label: 'Relatorios', icon: BarChart2 },
    { id: 'alertas', label: 'Alertas', icon: AlertTriangle },
  ];

  const pageDisplayNames = {
    dashboard: 'Dashboard', clientes: 'Clientes', motoristas: 'Motoristas',
    veiculos: 'Veiculos', cacambas: 'Cacambas', ctrs: 'CTRs',
    rotas: 'Rotas', medicao: 'Medicao', relatorios: 'Relatorios', alertas: 'Alertas'
  };

  // ---- FORMS ----
  const ClienteForm = () => {
    const [f, setF] = useState({ nome: '', cpf_cnpj: '', endereco: '', telefone: '', email: '' });
    return (
      <Modal title="Novo Cliente" onClose={() => setModal(null)}>
        <Input label="Nome *" value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="Nome completo" />
        <Input label="CPF/CNPJ *" value={f.cpf_cnpj} onChange={e => setF({ ...f, cpf_cnpj: e.target.value })} placeholder="000.000.000-00" />
        <Input label="Endereco *" value={f.endereco} onChange={e => setF({ ...f, endereco: e.target.value })} placeholder="Rua, numero, bairro" />
        <Input label="Telefone *" value={f.telefone} onChange={e => setF({ ...f, telefone: e.target.value })} placeholder="(00) 00000-0000" />
        <Input label="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="email@exemplo.com" />
        <button onClick={() => handleCreate('clientes', f, 'Cliente')} className="w-full mt-2 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">Cadastrar Cliente</button>
      </Modal>
    );
  };

  const MotoristaForm = () => {
    const [f, setF] = useState({ nome: '', cnh: '', telefone: '', status: 'disponivel' });
    return (
      <Modal title="Novo Motorista" onClose={() => setModal(null)}>
        <Input label="Nome *" value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="Nome completo" />
        <Input label="CNH *" value={f.cnh} onChange={e => setF({ ...f, cnh: e.target.value })} placeholder="Numero da CNH" />
        <Input label="Telefone *" value={f.telefone} onChange={e => setF({ ...f, telefone: e.target.value })} placeholder="(00) 00000-0000" />
        <Select label="Status" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="disponivel">Disponivel</option>
          <option value="em_rota">Em Rota</option>
          <option value="indisponivel">Indisponivel</option>
        </Select>
        <button onClick={() => handleCreate('motoristas', f, 'Motorista')} className="w-full mt-2 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">Cadastrar Motorista</button>
      </Modal>
    );
  };

  const VeiculoForm = () => {
    const [f, setF] = useState({ placa: '', modelo: '', capacidade: '', status: 'disponivel' });
    return (
      <Modal title="Novo Veiculo" onClose={() => setModal(null)}>
        <Input label="Placa *" value={f.placa} onChange={e => setF({ ...f, placa: e.target.value })} placeholder="ABC-1234" />
        <Input label="Modelo *" value={f.modelo} onChange={e => setF({ ...f, modelo: e.target.value })} placeholder="Ex: Mercedes-Benz Atego" />
        <Input label="Capacidade *" value={f.capacidade} onChange={e => setF({ ...f, capacidade: e.target.value })} placeholder="Capacidade em m3" />
        <Select label="Status" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="disponivel">Disponivel</option>
          <option value="em_uso">Em Uso</option>
          <option value="manutencao">Manutencao</option>
        </Select>
        <button onClick={() => handleCreate('veiculos', f, 'Veiculo')} className="w-full mt-2 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">Cadastrar Veiculo</button>
      </Modal>
    );
  };

  const CacambaForm = () => {
    const [f, setF] = useState({ numero: '', tamanho: '', localizacao: 'base', cliente_id: '' });
    return (
      <Modal title="Nova Cacamba" onClose={() => setModal(null)}>
        <Input label="Numero *" value={f.numero} onChange={e => setF({ ...f, numero: e.target.value })} placeholder="Ex: 001" />
        <Select label="Tamanho *" value={f.tamanho} onChange={e => setF({ ...f, tamanho: e.target.value })}>
          <option value="">Selecione...</option>
          <option value="3m3">3 m3</option>
          <option value="5m3">5 m3</option>
          <option value="7m3">7 m3</option>
          <option value="10m3">10 m3</option>
        </Select>
        <Select label="Localizacao" value={f.localizacao} onChange={e => setF({ ...f, localizacao: e.target.value })}>
          <option value="base">Base</option>
          <option value="cliente">Cliente</option>
        </Select>
        {f.localizacao === 'cliente' && (
          <Select label="Cliente" value={f.cliente_id} onChange={e => setF({ ...f, cliente_id: e.target.value })}>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
        )}
        <button onClick={() => handleCreate('cacambas', { ...f, cliente_id: f.cliente_id || null }, 'Cacamba')} className="w-full mt-2 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">Cadastrar Cacamba</button>
      </Modal>
    );
  };

  const CtrForm = () => {
    const [f, setF] = useState({ nome: '', endereco: '', cnpj: '', licenca_ambiental: '' });
    return (
      <Modal title="Novo CTR" onClose={() => setModal(null)}>
        <Input label="Nome *" value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="Nome do local de descarte" />
        <Input label="Endereco *" value={f.endereco} onChange={e => setF({ ...f, endereco: e.target.value })} placeholder="Endereco completo" />
        <Input label="CNPJ" value={f.cnpj} onChange={e => setF({ ...f, cnpj: e.target.value })} placeholder="00.000.000/0000-00" />
        <Input label="Licenca Ambiental" value={f.licenca_ambiental} onChange={e => setF({ ...f, licenca_ambiental: e.target.value })} placeholder="Numero da licenca" />
        <button onClick={() => handleCreate('ctrs', f, 'CTR')} className="w-full mt-2 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition">Cadastrar CTR</button>
      </Modal>
    );
  };

  const EditRotaForm = () => {
    const rota = editandoRotaData;
    const hasConcluidas = (rota?.operacoes || []).some(op => op.status === 'concluida');
    const [saving, setSaving] = useState(false);

    const toLocalDT = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toISOString().slice(0, 16); } catch { return ''; }
    };

    const [f, setF] = useState({
      nome: rota?.nome || '',
      data_hora: toLocalDT(rota?.data_hora),
      motorista_id: rota?.motorista_id ? String(rota.motorista_id) : '',
      veiculo_id: rota?.veiculo_id ? String(rota.veiculo_id) : '',
      observacoes: rota?.observacoes || ''
    });

    const [ops, setOps] = useState(
      (rota?.operacoes || []).map(op => ({
        tipo: op.tipo,
        cacamba_id: op.cacamba_id ? String(op.cacamba_id) : '',
        cacamba_entregar_id: op.cacamba_entregar_id ? String(op.cacamba_entregar_id) : '',
        cliente_id: op.cliente_id ? String(op.cliente_id) : '',
        cliente_entregar_id: op.cliente_entregar_id ? String(op.cliente_entregar_id) : '',
        cliente_nome_auto: op.cliente_nome || '',
        observacoes: op.observacoes || ''
      }))
    );

    if (!rota) return null;

    const addOp = () => setOps([...ops, { tipo: 'entregar', cacamba_id: '', cacamba_entregar_id: '', cliente_id: '', cliente_entregar_id: '', cliente_nome_auto: '', observacoes: '' }]);
    const removeOp = (i) => setOps(ops.filter((_, idx) => idx !== i));

    const getCacambasParaOp = (tipo, currentCacambaId) => {
      let filtered;
      if (tipo === 'entregar') filtered = cacambas.filter(c => c.localizacao_atual === 'base');
      else if (tipoRecolheDoCLiente(tipo)) filtered = cacambas.filter(c => c.localizacao_atual === 'cliente');
      else filtered = [...cacambas];
      if (currentCacambaId) {
        const current = cacambas.find(c => c.id === parseInt(currentCacambaId));
        if (current && !filtered.find(c => c.id === current.id)) filtered = [current, ...filtered];
      }
      return filtered;
    };

    const getCacambasEntregarParaOp = (tipo, currentCacambaId) => {
      let filtered;
      if (tipo === 'recolher_entregar') {
        filtered = cacambas.filter(c => c.localizacao_atual === 'cliente');
      } else {
        filtered = cacambas.filter(c => c.localizacao_atual === 'base');
      }
      if (currentCacambaId) {
        const current = cacambas.find(c => c.id === parseInt(currentCacambaId));
        if (current && !filtered.find(c => c.id === current.id)) filtered = [current, ...filtered];
      }
      return filtered;
    };

    const updateOp = (i, field, val) => {
      const n = [...ops];
      n[i] = { ...n[i], [field]: val };
      if (field === 'tipo') { n[i].cacamba_id = ''; n[i].cacamba_entregar_id = ''; n[i].cliente_id = ''; n[i].cliente_entregar_id = ''; n[i].cliente_nome_auto = ''; }
      if (field === 'cacamba_id' && tipoRecolheDoCLiente(n[i].tipo) && val) {
        const cab = cacambas.find(c => c.id === parseInt(val));
        if (cab && cab.cliente_id) { n[i].cliente_id = String(cab.cliente_id); n[i].cliente_nome_auto = cab.cliente_nome || ''; }
        else { n[i].cliente_id = ''; n[i].cliente_nome_auto = ''; }
      }
      setOps(n);
    };

    const submit = async () => {
      setSaving(true);
      try {
        const payload = { nome: f.nome || null, data_hora: f.data_hora, motorista_id: f.motorista_id || null, veiculo_id: f.veiculo_id || null, observacoes: f.observacoes || null };
        if (!hasConcluidas) {
          payload.operacoes = ops.map(o => ({
            tipo: o.tipo,
            cacamba_id: parseInt(o.cacamba_id),
            cacamba_entregar_id: tipoTemCacambaEntregar(o.tipo) && o.cacamba_entregar_id ? parseInt(o.cacamba_entregar_id) : null,
            cliente_id: o.cliente_id ? parseInt(o.cliente_id) : null,
            cliente_entregar_id: o.tipo === 'recolher_entregar' && o.cliente_entregar_id ? parseInt(o.cliente_entregar_id) : null,
            observacoes: o.observacoes || null
          }));
        }
        const res = await fetchJSON(`${API}/rotas/${rota.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        if (res.error) { showToast(res.error, 'error'); return; }
        showToast('Rota atualizada com sucesso!');
        setShowEditarRota(false); setEditandoRotaData(null);
        load('rotas'); load('cacambas'); load('motoristas'); load('veiculos'); loadParadas();
      } catch { showToast('Erro ao atualizar rota', 'error'); } finally { setSaving(false); }
    };

    return (
      <BigModal title={`Editar: ${rota.nome || 'Rota #' + rota.id}`} onClose={() => { setShowEditarRota(false); setEditandoRotaData(null); }}>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Nome da Rota" value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="Ex: Rota manha" />
          <Input label="Data/Hora *" type="datetime-local" value={f.data_hora} onChange={e => setF({ ...f, data_hora: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Motorista" value={f.motorista_id} onChange={e => setF({ ...f, motorista_id: e.target.value })}>
            <option value="">Selecione...</option>
            {motoristas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </Select>
          <Select label="Veiculo" value={f.veiculo_id} onChange={e => setF({ ...f, veiculo_id: e.target.value })}>
            <option value="">Selecione...</option>
            {veiculos.map(v => <option key={v.id} value={v.id}>{v.placa} - {v.modelo}</option>)}
          </Select>
        </div>
        <Input label="Observacoes" value={f.observacoes} onChange={e => setF({ ...f, observacoes: e.target.value })} placeholder="Observacoes opcionais" />
        <div className="mt-4 mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-700">Operacoes</h3>
          {hasConcluidas ? (
            <span className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full font-medium">Operacoes concluidas - nao editavel</span>
          ) : (
            <button onClick={addOp} className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium hover:bg-green-200 flex items-center gap-1"><Plus size={14} /> Adicionar</button>
          )}
        </div>
        {hasConcluidas ? (
          <div className="space-y-2 mb-4">
            {(rota.operacoes || []).map((op, i) => {
              const tb = tipoBadge(op.tipo);
              return (
                <div key={i} className={`border rounded-lg p-3 ${op.status === 'concluida' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${op.status === 'concluida' ? 'bg-green-500 text-white' : 'bg-gray-300 text-white'}`}>
                      {op.status === 'concluida' ? <Check size={12} /> : i + 1}
                    </span>
                    <Badge text={tb.text} color={tb.color} />
                    <OpLabel op={op} />
                    {op.cliente_nome && <><ArrowRight size={12} className="text-gray-400" /><span className="text-gray-500">{op.cliente_nome}</span></>}
                  </div>
                  {op.observacoes && <p className="text-xs text-gray-500 mt-1 ml-8 italic">Obs: {op.observacoes}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {ops.map((op, i) => {
              const isTrocar = op.tipo === 'trocar';
              const isRecolher = op.tipo === 'recolher';
              const isEntregar = op.tipo === 'entregar';
              const isRecEnt = op.tipo === 'recolher_entregar';
              const cacambasFiltradas = getCacambasParaOp(op.tipo, op.cacamba_id);
              const cacambasEntregarFiltradas = getCacambasEntregarParaOp(op.tipo, op.cacamba_entregar_id);
    const openAddCliente = (slot) => setQuickAdd({
      tipo: 'cliente', opIndex: i, slot,
      onCreated: (novoId) => {
        // Pre-seleciona o cliente novo na operacao certa.
        // Como setOps fecha sobre 'i', usamos updateOp para reaproveitar a logica de auto-fill.
        updateOp(i, slot, String(novoId));
      }
    });
    const openAddCacamba = (slot) => setQuickAdd({
      tipo: 'cacamba', opIndex: i, slot,
      contexto: {
        // Define onde a nova cacamba vai nascer com base no tipo da operacao + slot
        localizacao: (slot === 'cacamba_entregar_id' && op.tipo === 'recolher_entregar')
          ? 'cliente' // cacamba que sera entregue partindo do cliente origem
          : (slot === 'cacamba_id' && (op.tipo === 'recolher' || op.tipo === 'trocar' || op.tipo === 'recolher_entregar'))
            ? 'cliente' // cacamba a recolher do cliente
            : 'base',   // cacamba a entregar (da base)
        cliente_id: (op.cliente_id || (op.cacamba_id && cacambas.find(c => c.id === parseInt(op.cacamba_id))?.cliente_id) || null)
      },
      onCreated: (novoId) => { updateOp(i, slot, String(novoId)); }
    });

              return (
                <div key={i} className="border rounded-lg p-3 mb-2 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-500">Operacao {i + 1}</span>
                    {ops.length > 1 && <button onClick={() => removeOp(i)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Select label="Tipo" value={op.tipo} onChange={e => updateOp(i, 'tipo', e.target.value)}>
                      <option value="entregar">Entregar</option>
                      <option value="recolher">Recolher</option>
                      <option value="trocar">Trocar</option>
                      <option value="recolher_entregar">Recolha + Entrega</option>
                    </Select>
                    {isEntregar && (
                      <>
                        <SelectWithAdd label="Cacamba (na base)" value={op.cacamba_id} onChange={e => updateOp(i, 'cacamba_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho})</option>)}
                        </SelectWithAdd>
                        <SelectWithAdd label="Cliente" value={op.cliente_id} onChange={e => updateOp(i, 'cliente_id', e.target.value)} onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                          <option value="">Selecione...</option>
                          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </SelectWithAdd>
                      </>
                    )}
                    {isRecolher && (
                      <>
                        <SelectWithAdd label="Cacamba (no cliente)" value={op.cacamba_id} onChange={e => updateOp(i, 'cacamba_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                        </SelectWithAdd>
                        {op.cliente_nome_auto ? (
                          <div className="mb-3"><label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label><div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-2 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div></div>
                        ) : (
                          <SelectWithAdd label="Cliente" value={op.cliente_id} onChange={e => updateOp(i, 'cliente_id', e.target.value)} onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                            <option value="">Selecione...</option>
                            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                          </SelectWithAdd>
                        )}
                      </>
                    )}
                    {isTrocar && (
                      <>
                        <SelectWithAdd label={'\u2191 Recolher (no cliente)'} value={op.cacamba_id} onChange={e => updateOp(i, 'cacamba_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                        </SelectWithAdd>
                        <SelectWithAdd label={'\u2193 Entregar (da base)'} value={op.cacamba_entregar_id} onChange={e => updateOp(i, 'cacamba_entregar_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_entregar_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasEntregarFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho})</option>)}
                        </SelectWithAdd>
                      </>
                    )}
                    {isRecEnt && (
                      <>
                        <SelectWithAdd label={'\u2191 Recolher (no cliente)'} value={op.cacamba_id} onChange={e => updateOp(i, 'cacamba_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                        </SelectWithAdd>
                        <SelectWithAdd label={'\u2193 Entregar (do cliente)'} value={op.cacamba_entregar_id} onChange={e => updateOp(i, 'cacamba_entregar_id', e.target.value)} onAdd={() => openAddCacamba('cacamba_entregar_id')} addTitle="Nova cacamba">
                          <option value="">Selecione...</option>
                          {cacambasEntregarFiltradas.map(c => <option key={c.id} value={c.id}>#{c.numero} ({c.tamanho}){c.cliente_nome ? ` - ${c.cliente_nome}` : ''}</option>)}
                        </SelectWithAdd>
                      </>
                    )}
                  </div>
                  {isTrocar && (
                    <div className="mt-2">
                      {op.cliente_nome_auto ? (
                        <div className="mb-2"><label className="block text-xs font-medium text-gray-500 mb-1">Cliente (auto)</label><div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-1.5 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div></div>
                      ) : (
                        <SelectWithAdd label="Cliente" value={op.cliente_id} onChange={e => updateOp(i, 'cliente_id', e.target.value)} onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                          <option value="">Selecione...</option>
                          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </SelectWithAdd>
                      )}
                    </div>
                  )}
                  {isRecEnt && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {op.cliente_nome_auto ? (
                        <div className="mb-2"><label className="block text-xs font-medium text-gray-500 mb-1">Cliente Recolha (auto)</label><div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-1.5 text-sm text-green-800 font-medium">{op.cliente_nome_auto}</div></div>
                      ) : (
                        <SelectWithAdd label="Cliente Recolha" value={op.cliente_id} onChange={e => updateOp(i, 'cliente_id', e.target.value)} onAdd={() => openAddCliente('cliente_id')} addTitle="Novo cliente">
                          <option value="">Selecione...</option>
                          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </SelectWithAdd>
                      )}
                      <SelectWithAdd label="Cliente Entrega *" value={op.cliente_entregar_id || ''} onChange={e => updateOp(i, 'cliente_entregar_id', e.target.value)} onAdd={() => openAddCliente('cliente_entregar_id')} addTitle="Novo cliente">
                        <option value="">Selecione...</option>
                        {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </SelectWithAdd>
                    </div>
                  )}
                  <div className="mt-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Observacao da operacao</label>
                    <input type="text" value={op.observacoes} onChange={e => updateOp(i, 'observacoes', e.target.value)} placeholder="Obs. opcional para esta operacao..." className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  {tipoRecolheDoCLiente(op.tipo) && !op.cacamba_id && <p className="text-xs text-amber-600 mt-1">Selecione a cacamba para preencher o cliente automaticamente</p>}
                </div>
              );
            })}
          </>
        )}
        <div className="flex gap-3 mt-4">
          <button onClick={() => { setShowEditarRota(false); setEditandoRotaData(null); }} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition text-sm">Cancelar</button>
          <button onClick={() => handleGerarPDF(rota.id)} className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition text-sm"><Download size={16} /> PDF Atual</button>
          <button onClick={submit} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
            {saving ? 'Salvando...' : 'Salvar e Gerar PDF'}
          </button>
        </div>
      </BigModal>
    );
  };

  // ---- HELPERS ----
  const statusBadge = (status) => {
    const map = {
      disponivel: { text: 'Disponivel', color: 'green' }, em_rota: { text: 'Em Rota', color: 'orange' },
      indisponivel: { text: 'Indisponivel', color: 'red' }, em_uso: { text: 'Em Uso', color: 'orange' },
      manutencao: { text: 'Manutencao', color: 'yellow' }, em_cliente: { text: 'Em Cliente', color: 'blue' },
    };
    const s = map[status] || { text: status || 'N/A', color: 'gray' };
    return <Badge text={s.text} color={s.color} />;
  };

  const rotaStatusBadge = (status) => {
    const map = {
      pendente: { text: 'Pendente', color: 'yellow' }, em_andamento: { text: 'Em Andamento', color: 'blue' },
      concluida: { text: 'Concluida', color: 'green' }, cancelada: { text: 'Cancelada', color: 'red' },
    };
    const s = map[status] || { text: status || 'N/A', color: 'gray' };
    return <Badge text={s.text} color={s.color} />;
  };

  const formatarData = (data) => { if (!data) return '-'; return new Date(data).toLocaleString('pt-BR'); };
  const formatMoney = (val) => parseFloat(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // ============================
  // ========== PAGES ===========
  // ============================

  const DashboardPage = () => {
    const naBase = cacambas.filter(c => c.localizacao_atual === 'base').length;
    const emCliente = cacambas.filter(c => c.localizacao_atual === 'cliente').length;
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Stat icon={Users} label="Clientes" value={clientes.length} color="blue" />
          <Stat icon={User} label="Motoristas" value={motoristas.length} color="green" />
          <Stat icon={Car} label="Veiculos" value={veiculos.length} color="purple" />
          <Stat icon={Package} label="Cacambas" value={cacambas.length} color="orange" />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Stat icon={Package} label="Na Base" value={naBase} color="green" />
          <Stat icon={MapPin} label="Em Cliente" value={emCliente} color="blue" />
          <Stat icon={AlertTriangle} label="Paradas +3 dias" value={paradas.length} color="red" />
        </div>
        {paradas.length > 0 && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="text-red-500" size={20} />
              <h3 className="font-bold text-red-700">Cacambas Paradas ha mais de 3 dias</h3>
            </div>
            {paradas.slice(0, 5).map(p => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded-lg p-3 mb-2">
                <div>
                  <span className="font-bold text-gray-800">Cacamba #{p.numero}</span>
                  <span className="text-sm text-gray-500 ml-2">({p.tamanho})</span>
                  {p.cliente_nome && <span className="text-sm text-gray-600 ml-2">{'\u2192'} {p.cliente_nome}</span>}
                </div>
                <Badge text={`${p.dias_parada} dias`} color="red" />
              </div>
            ))}
          </div>
        )}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2"><Navigation size={18} /> Ultimas Rotas</h3>
          {rotas.length === 0 ? <p className="text-gray-400 text-sm text-center py-4">Nenhuma rota registrada</p> : rotasOrdenadas.slice(0, 5).map(r => (
            <div key={r.id} className="flex items-center justify-between border-b last:border-0 py-3">
              <div>
                <p className="font-medium text-gray-800">{r.nome}</p>
                <p className="text-xs text-gray-500">{r.motorista_nome || 'Sem motorista'} {'\u2022'} {r.veiculo_placa || 'Sem veiculo'} {'\u2022'} {r.operacoes?.length || 0} ops</p>
              </div>
              <div className="flex items-center gap-2">
                {rotaStatusBadge(r.status)}
                <span className="text-xs text-gray-400">{r.data_hora ? new Date(r.data_hora).toLocaleDateString('pt-BR') : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ClientesPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Clientes</h1>
        <button onClick={() => setModal('cliente')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus size={16} /> Novo Cliente</button>
      </div>
      {clientes.length === 0 ? <EmptyState icon={Users} message="Nenhum cliente cadastrado" /> : (
        <div className="space-y-3">
          {clientes.map(c => (
            <div key={c.id} className="bg-white border rounded-xl p-4 flex items-start justify-between hover:shadow-md transition">
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{c.nome}</h3>
                <div className="mt-1 space-y-0.5">
                  <p className="text-sm text-gray-500 flex items-center gap-1"><Hash size={12} /> {c.cpf_cnpj}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} /> {c.endereco}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1"><Phone size={12} /> {c.telefone}</p>
                  {c.email && <p className="text-sm text-gray-500 flex items-center gap-1"><Mail size={12} /> {c.email}</p>}
                </div>
              </div>
              <button onClick={() => handleDelete('clientes', c.id, 'Cliente')} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const MotoristasPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Motoristas</h1>
        <button onClick={() => setModal('motorista')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus size={16} /> Novo Motorista</button>
      </div>
      {motoristas.length === 0 ? <EmptyState icon={User} message="Nenhum motorista cadastrado" /> : (
        <div className="grid grid-cols-1 gap-3">
          {motoristas.map(m => (
            <div key={m.id} className="bg-white border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><User size={20} className="text-green-600" /></div>
                <div>
                  <h3 className="font-bold text-gray-800">{m.nome}</h3>
                  <p className="text-sm text-gray-500">CNH: {m.cnh} {'\u2022'} Tel: {m.telefone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(m.status)}
                <button onClick={() => handleDelete('motoristas', m.id, 'Motorista')} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const VeiculosPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Veiculos</h1>
        <button onClick={() => setModal('veiculo')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus size={16} /> Novo Veiculo</button>
      </div>
      {veiculos.length === 0 ? <EmptyState icon={Car} message="Nenhum veiculo cadastrado" /> : (
        <div className="grid grid-cols-1 gap-3">
          {veiculos.map(v => (
            <div key={v.id} className="bg-white border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center"><Car size={20} className="text-purple-600" /></div>
                <div>
                  <h3 className="font-bold text-gray-800">{v.placa}</h3>
                  <p className="text-sm text-gray-500">{v.modelo} {'\u2022'} Cap: {v.capacidade}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(v.status)}
                <button onClick={() => handleDelete('veiculos', v.id, 'Veiculo')} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const CacambasPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Cacambas</h1>
        <button onClick={() => setModal('cacamba')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus size={16} /> Nova Cacamba</button>
      </div>
      {cacambas.length === 0 ? <EmptyState icon={Package} message="Nenhuma cacamba cadastrada" /> : (
        <div className="grid grid-cols-1 gap-3">
          {cacambas.map(c => (
            <div key={c.id} className="bg-white border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${c.localizacao_atual === 'base' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>#{c.numero}</div>
                <div>
                  <h3 className="font-bold text-gray-800">Cacamba #{c.numero} <span className="font-normal text-sm text-gray-500">({c.tamanho})</span></h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} />{c.localizacao_atual === 'base' ? 'Na base' : `Cliente: ${c.cliente_nome || 'N/A'}`}</p>
                  {c.data_ultima_movimentacao && <p className="text-xs text-gray-400 flex items-center gap-1"><Clock size={11} /> Ultima mov: {new Date(c.data_ultima_movimentacao).toLocaleDateString('pt-BR')}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(c.status || (c.localizacao_atual === 'base' ? 'disponivel' : 'em_cliente'))}
                <button onClick={() => handleDelete('cacambas', c.id, 'Cacamba')} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const CtrsPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">CTRs - Locais de Descarte</h1>
        <button onClick={() => setModal('ctr')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus size={16} /> Novo CTR</button>
      </div>
      {ctrs.length === 0 ? <EmptyState icon={FileText} message="Nenhum CTR cadastrado" /> : (
        <div className="space-y-3">
          {ctrs.map(c => (
            <div key={c.id} className="bg-white border rounded-xl p-4 flex items-start justify-between hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0"><FileText size={20} className="text-teal-600" /></div>
                <div>
                  <h3 className="font-bold text-gray-800">{c.nome}</h3>
                  <div className="mt-1 space-y-0.5">
                    <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} /> {c.endereco}</p>
                    {c.cnpj && <p className="text-sm text-gray-500 flex items-center gap-1"><Hash size={12} /> CNPJ: {c.cnpj}</p>}
                    {c.licenca_ambiental && <p className="text-sm text-gray-500 flex items-center gap-1"><FileText size={12} /> Licenca: {c.licenca_ambiental}</p>}
                  </div>
                </div>
              </div>
              <button onClick={() => handleDelete('ctrs', c.id, 'CTR')} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // [PATCH:rotas_layout_v2] + [PATCH:rotas_layout_v2_hotfix1] + [PATCH:rotas_v3]
  const RotasPage = () => {
    const toggleExpand = (key) => setRotasExpandidas(prev => ({ ...prev, [key]: !prev[key] }));

    // 1. Agrupar por placa + dia
    const grupos = (() => {
      const map = new Map();
      for (const r of rotasOrdenadas) {
        const placa = r.veiculo_placa || 'SEM_VEICULO';
        const dia   = r.data_hora ? new Date(r.data_hora).toISOString().slice(0,10) : 'sem_data';
        const key   = placa + '|' + dia;
        if (!map.has(key)) map.set(key, { key, placa, dia, veiculo_modelo: r.veiculo_modelo, data_hora: r.data_hora, rotas: [] });
        map.get(key).rotas.push(r);
      }
      return Array.from(map.values()).sort((a,b) => new Date(b.data_hora||0) - new Date(a.data_hora||0));
    })();

    // 2. Filtrar
    const gruposFiltrados = grupos.filter(g => {
      if (rotaFiltro === 'pendente'  && g.rotas.every(r => r.status === 'concluida'))   return false;
      if (rotaFiltro === 'concluida' && g.rotas.every(r => r.status !== 'concluida'))   return false;
      if (rotaBusca.trim()) {
        const q = rotaBusca.toLowerCase();
        const hay = g.rotas.map(r => [
          r.nome, r.motorista_nome, r.veiculo_placa, r.veiculo_modelo,
          String(r.id), r.observacoes,
          ...(r.operacoes||[]).map(op => op.cliente_nome||''),
          ...(r.operacoes||[]).map(op => op.cacamba_numero ? '#'+op.cacamba_numero : ''),
        ].filter(Boolean).join(' ')).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const counts = {
      todas:    grupos.length,
      pendente: grupos.filter(g => g.rotas.some(r => r.status !== 'concluida')).length,
      concluida:grupos.filter(g => g.rotas.every(r => r.status === 'concluida')).length,
    };

    const FilterChip = ({ id, label, color }) => {
      const active = rotaFiltro === id;
      const p = {
        gray:  active ? 'bg-gray-800 text-white border-gray-800'   : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
        amber: active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400',
        green: active ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-400',
      };
      return (
        <button onClick={() => setRotaFiltro(id)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${p[color]}`}>
          {label}
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>{counts[id]}</span>
        </button>
      );
    };

    // 3. Acoes: direto se 1 rota, picker se multiplas
    const executarAcao = (g, acao) => {
      if (g.rotas.length === 1) {
        const r = g.rotas[0];
        if (acao === 'detalhes')  return abrirDetalhes(r);
        if (acao === 'editar')    return abrirEditarRota(r);
        if (acao === 'finalizar') return abrirFinalizar(r);
        if (acao === 'pdf')       return handleGerarPDF(r.id);
        if (acao === 'excluir')   return handleDelete('rotas', r.id, 'Rota');
      } else {
        setGrupoPickerOpen({ grupo: g, acao });
      }
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Rotas</h1>
          <button onClick={() => setModal('rota')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 text-sm shadow-sm">
            <Plus size={16}/> Nova Rota
          </button>
        </div>

        {/* Filtros + Busca */}
        <div className="bg-white border rounded-xl p-3 mb-5 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip id="todas"     label="Todas"       color="gray"  />
            <FilterChip id="pendente"  label="Em aberto"   color="amber" />
            <FilterChip id="concluida" label="Finalizadas" color="green" />
          </div>
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input type="text" value={rotaBusca} onChange={e => setRotaBusca(e.target.value)}
              placeholder="Buscar por placa, motorista, cliente, cacamba..."
              className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"/>
          </div>
        </div>

        {gruposFiltrados.length === 0
          ? <EmptyState icon={Navigation} message={grupos.length === 0 ? 'Nenhuma rota registrada' : 'Nenhuma rota corresponde ao filtro'}/>
          : (
          <div className="space-y-3">
            {gruposFiltrados.map(g => {
              const todasConcluidas = g.rotas.every(r => r.status === 'concluida');
              const algumaConcluida = g.rotas.some(r => r.status === 'concluida');
              const algumaPendente  = g.rotas.some(r => r.status !== 'concluida');
              const todasOps    = g.rotas.flatMap(r => r.operacoes || []);
              const totalOps    = todasOps.length;
              const opsConc     = todasOps.filter(o => o.status === 'concluida').length;
              const expandida   = !!rotasExpandidas[g.key];
              const multiRota   = g.rotas.length > 1;

              const stripeColor = todasConcluidas ? 'bg-green-500' : algumaConcluida ? 'bg-blue-500' : 'bg-amber-400';
              const dataObj     = g.data_hora ? new Date(g.data_hora) : null;
              const dataValida  = dataObj && !isNaN(dataObj.getTime());
              const dataDia     = dataValida ? dataObj.toLocaleDateString('pt-BR',{day:'2-digit',month:'short'}) : '--';
              const progPct     = totalOps === 0 ? 0 : Math.round((opsConc / totalOps) * 100);

              return (
                <div key={g.key} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition">
                  <div className="flex">
                    <div className={`w-1.5 flex-shrink-0 ${stripeColor}`}/>
                    <div className="flex-1 min-w-0">

                      {/* Cabecalho */}
                      <div className="p-4 flex items-start gap-4">
                        {/* Data */}
                        <div className="flex-shrink-0 w-14 text-center bg-gray-50 border border-gray-200 rounded-lg py-2">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-bold">{dataValida ? dataDia.split(' ')[1] : ''}</p>
                          <p className="text-xl font-bold text-gray-800 leading-none">{dataValida ? dataDia.split(' ')[0] : '--'}</p>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-gray-800 flex items-center gap-1">
                              <Car size={14} className="text-gray-400"/>
                              {g.placa === 'SEM_VEICULO' ? 'Sem veiculo' : g.placa}
                            </h3>
                            {g.veiculo_modelo && <span className="text-xs text-gray-400">({g.veiculo_modelo})</span>}
                            {multiRota && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{g.rotas.length} rotas</span>}
                            {todasConcluidas
                              ? <Badge text="Finalizado" color="green"/>
                              : algumaConcluida ? <Badge text="Parcial" color="blue"/>
                              : <Badge text="Em aberto" color="yellow"/>}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <User size={12}/>
                              {(() => { const ms=[...new Set(g.rotas.map(r=>r.motorista_nome).filter(Boolean))]; return ms.length?ms.join(', '):'Sem motorista'; })()}
                            </span>
                            <span className="flex items-center gap-1"><Package size={12}/> {totalOps} op(s)</span>
                            {multiRota && <span className="text-[10px] text-blue-500">{g.rotas.map(r=>r.nome||'#'+r.id).join(' + ')}</span>}
                          </div>
                          {totalOps > 0 && (
                            <div className="mt-2 flex items-center gap-2">
                              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden max-w-[200px]">
                                <div className={`h-full transition-all ${todasConcluidas?'bg-green-500':'bg-blue-500'}`} style={{width:`${progPct}%`}}/>
                              </div>
                              <span className="text-[10px] text-gray-400">{opsConc}/{totalOps}</span>
                            </div>
                          )}
                        </div>

                        {/* Acoes */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {algumaPendente && (
                            <button onClick={() => executarAcao(g,'finalizar')}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-xs font-semibold shadow-sm">
                              <CheckCircle size={14}/> Finalizar
                            </button>
                          )}
                          <button onClick={() => executarAcao(g,'detalhes')} title="Detalhes"
                            className="p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition"><Eye size={16}/></button>
                          {algumaPendente && (
                            <button onClick={() => executarAcao(g,'editar')} title="Editar"
                              className="p-2 text-gray-500 hover:bg-amber-50 hover:text-amber-600 rounded-lg transition"><Edit3 size={16}/></button>
                          )}
                          <button onClick={() => executarAcao(g,'pdf')} title="PDF"
                            className="p-2 text-gray-500 hover:bg-green-50 hover:text-green-600 rounded-lg transition"><Download size={16}/></button>
                          <button onClick={() => executarAcao(g,'excluir')}
                            disabled={todasConcluidas}
                            title={todasConcluidas ? 'Rota finalizada nao pode ser excluida' : 'Excluir'}
                            className={`p-2 rounded-lg transition ${todasConcluidas?'text-gray-300 cursor-not-allowed':'text-gray-400 hover:bg-red-50 hover:text-red-600'}`}>
                            <Trash2 size={16}/>
                          </button>
                        </div>
                      </div>

                      {/* Toggle operacoes */}
                      {totalOps > 0 && (
                        <button onClick={() => toggleExpand(g.key)}
                          className="w-full px-4 py-2 border-t border-gray-100 bg-gray-50/70 hover:bg-gray-100 transition flex items-center justify-between text-xs text-gray-600 font-medium">
                          <span className="flex items-center gap-2">
                            <ChevronDown size={14} className={`transition-transform ${expandida?'rotate-180':''}`}/>
                            {expandida ? 'Ocultar operacoes' : `Ver ${totalOps} operacao(oes)`}
                          </span>
                          <span className="flex items-center gap-3 text-[11px]">
                            {todasOps.filter(o=>o.tipo==='entregar').length>0 && <span className="text-blue-600">{'\u2193'} {todasOps.filter(o=>o.tipo==='entregar').length}</span>}
                            {todasOps.filter(o=>o.tipo==='recolher').length>0 && <span className="text-green-600">{'\u2191'} {todasOps.filter(o=>o.tipo==='recolher').length}</span>}
                            {todasOps.filter(o=>o.tipo==='trocar').length>0 && <span className="text-purple-600">{'\u2194'} {todasOps.filter(o=>o.tipo==='trocar').length}</span>}
                            {todasOps.filter(o=>o.tipo==='recolher_entregar').length>0 && <span className="text-orange-600">{'\u2191\u2193'} {todasOps.filter(o=>o.tipo==='recolher_entregar').length}</span>}
                          </span>
                        </button>
                      )}

                      {/* Lista de operacoes expandida */}
                      {expandida && totalOps > 0 && (
                        <div className="border-t border-gray-100 bg-white">
                          {g.rotas.map((r, rIdx) => (
                            <div key={r.id} className={rIdx > 0 ? 'border-t-2 border-blue-100' : ''}>
                              {multiRota && (
                                <div className="px-4 py-2 bg-blue-50 flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="font-bold text-blue-700">{r.nome || `Rota #${r.id}`}</span>
                                    {rotaStatusBadge(r.status)}
                                    {r.motorista_nome && <span className="text-gray-500">{'\u2022'} {r.motorista_nome}</span>}
                                  </div>
                                  <div className="flex gap-1">
                                    <button onClick={() => abrirDetalhes(r)} className="p-1 text-gray-400 hover:text-blue-600"><Eye size={13}/></button>
                                    {r.status !== 'concluida' && <button onClick={() => abrirEditarRota(r)} className="p-1 text-gray-400 hover:text-amber-600"><Edit3 size={13}/></button>}
                                    <button onClick={() => handleGerarPDF(r.id)} className="p-1 text-gray-400 hover:text-green-600"><Download size={13}/></button>
                                    {r.status !== 'concluida' && <button onClick={() => abrirFinalizar(r)} className="p-1 text-amber-400 hover:text-amber-600"><CheckCircle size={13}/></button>}
                                  </div>
                                </div>
                              )}
                              {(r.operacoes||[]).map((op,i) => {
                                const tb = tipoBadge(op.tipo);
                                const conc = op.status === 'concluida';
                                return (
                                  <div key={op.id||i} className={`px-4 py-3 flex items-start gap-3 ${i<(r.operacoes||[]).length-1?'border-b border-gray-50':''}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5 ${conc?'bg-green-500 text-white':'bg-gray-200 text-gray-500'}`}>
                                      {conc ? <Check size={11}/> : (op.ordem||i+1)}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap text-sm">
                                        <Badge text={tb.text} color={tb.color}/>
                                        <OpLabel op={op}/>
                                        {op.cliente_nome && <><ArrowRight size={11} className="text-gray-300"/><span className="text-gray-700 font-medium">{op.cliente_nome}</span></>}
                                      </div>
                                      <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                                        {tipoPrecisaCtr(op.tipo) && op.ctr_nome && <span className="flex items-center gap-1 text-teal-600"><FileText size={10}/> {op.ctr_nome}</span>}
                                        {op.fotos && op.fotos.length > 0 && <span className="flex items-center gap-1"><Camera size={10}/> {op.fotos.length}</span>}
                                        {op.observacoes && <span className="italic truncate max-w-[300px]">{op.observacoes}</span>}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Picker: escolher qual rota quando grupo tem multiplas */}
        {grupoPickerOpen && (
          <Modal
            title={
              grupoPickerOpen.acao === 'editar'    ? 'Qual rota editar?'    :
              grupoPickerOpen.acao === 'finalizar' ? 'Qual rota finalizar?' :
              grupoPickerOpen.acao === 'pdf'       ? 'PDF de qual rota?'    :
              grupoPickerOpen.acao === 'excluir'   ? 'Excluir qual rota?'   : 'Escolha uma rota'
            }
            onClose={() => setGrupoPickerOpen(null)}
          >
            <p className="text-sm text-gray-500 mb-3">
              {grupoPickerOpen.grupo.rotas.length} rotas para {grupoPickerOpen.grupo.placa} neste dia:
            </p>
            <div className="space-y-2">
              {grupoPickerOpen.grupo.rotas.map(r => {
                const bloqueada = (grupoPickerOpen.acao === 'editar' || grupoPickerOpen.acao === 'finalizar' || grupoPickerOpen.acao === 'excluir') && r.status === 'concluida';
                return (
                  <button key={r.id} disabled={bloqueada}
                    onClick={() => {
                      const a = grupoPickerOpen.acao; setGrupoPickerOpen(null);
                      if (a==='editar')    abrirEditarRota(r);
                      if (a==='finalizar') abrirFinalizar(r);
                      if (a==='pdf')       handleGerarPDF(r.id);
                      if (a==='detalhes')  abrirDetalhes(r);
                      if (a==='excluir')   handleDelete('rotas', r.id, 'Rota');
                    }}
                    className={`w-full text-left p-3 rounded-lg border transition flex items-center justify-between ${bloqueada?'opacity-50 cursor-not-allowed border-gray-100':'border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-gray-800 text-sm">{r.nome || `Rota #${r.id}`}</span>
                        {rotaStatusBadge(r.status)}
                      </div>
                      <p className="text-xs text-gray-500">
                        {r.data_hora ? new Date(r.data_hora).toLocaleString('pt-BR') : ''} {'\u2022'} {r.operacoes?.length||0} op(s)
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-gray-400"/>
                  </button>
                );
              })}
            </div>
          </Modal>
        )}

      </div>
    );
  };

  const MedicaoPage = () => {
    const clienteSel = clientes.find(c => c.id === parseInt(fatClienteId));
    const allSelected = fatOperacoes.length > 0 && fatOperacoes.every(op => fatSelecionadas[op.id]?.selecionada);
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Medicao</h1>
        <div className="flex gap-1 mb-6">
          <button onClick={() => setMedicaoTab('nova')} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition ${medicaoTab === 'nova' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border'}`}>
            <span className="flex items-center gap-2"><Plus size={15} /> Nova Medicao</span>
          </button>
          <button onClick={() => { setMedicaoTab('historico'); loadMedicoesHistorico(); }} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition ${medicaoTab === 'historico' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border'}`}>
            <span className="flex items-center gap-2"><Clock size={15} /> Historico de Medicoes</span>
          </button>
        </div>
        {medicaoTab === 'nova' && (
          <>
            <div className="bg-white border rounded-xl p-5 mb-6">
              <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2"><Search size={16} /> Buscar Operacoes para Medicao</h3>
              <div className="grid grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                  <select value={fatClienteId} onChange={e => { setFatClienteId(e.target.value); setFatOperacoes([]); setFatSelecionadas({}); }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
                    <option value="">Selecione o cliente...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicio</label>
                  <input type="date" value={fatDataInicio} onChange={e => setFatDataInicio(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input type="date" value={fatDataFim} onChange={e => setFatDataFim(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <button onClick={buscarOperacoesFaturamento} disabled={fatBuscando || !fatClienteId} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed h-10">
                  {fatBuscando ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                  {fatBuscando ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
            </div>
            {clienteSel && fatOperacoes.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center justify-between">
                <div>
                  <p className="font-bold text-blue-800">{clienteSel.nome}</p>
                  <p className="text-sm text-blue-600">{clienteSel.cpf_cnpj} {'\u2022'} {clienteSel.telefone}</p>
                </div>
                <Badge text={`${fatOperacoes.length} operacoes encontradas`} color="blue" />
              </div>
            )}
            {fatOperacoes.length > 0 && (
              <div className="bg-white border rounded-xl overflow-hidden mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left"><input type="checkbox" checked={allSelected} onChange={fatToggleAll} className="rounded border-gray-300 w-4 h-4" /></th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Rota</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Cacamba</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">CTR</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Fotos</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Valor Locacao</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Valor CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fatOperacoes.map(op => {
                        const sel = fatSelecionadas[op.id] || {};
                        return (
                          <tr key={op.id} className={`border-b last:border-0 transition ${sel.selecionada ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                            <td className="px-4 py-3"><input type="checkbox" checked={!!sel.selecionada} onChange={() => fatToggleSelect(op.id)} className="rounded border-gray-300 w-4 h-4" /></td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{op.data_hora ? new Date(op.data_hora).toLocaleDateString('pt-BR') : '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{op.rota_nome || `Rota #${op.rota_id}`}</td>
                            <td className="px-4 py-3"><span className="text-sm font-medium text-gray-800">#{op.cacamba_numero}</span><span className="text-xs text-gray-500 ml-1">({op.cacamba_tamanho})</span></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{op.ctr_nome || <span className="text-gray-300">-</span>}</td>
                            <td className="px-4 py-3">{op.fotos && op.fotos.length > 0 ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{op.fotos.length} foto(s)</span> : <span className="text-xs text-gray-300">0</span>}</td>
                            <td className="px-4 py-3">
                              {op.faturado ? (
                                <div className="flex flex-col gap-1">
                                  <Badge text="Faturado" color="green" />
                                  {op.data_faturamento && <span className="text-xs text-gray-400">em {new Date(op.data_faturamento).toLocaleDateString('pt-BR')}</span>}
                                  <button onClick={() => fatEditarOperacao(op)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold mt-0.5"><Edit3 size={12} /> Editar</button>
                                </div>
                              ) : <Badge text="Pendente" color="yellow" />}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-400">R$</span>
                                <input type="number" step="0.01" min="0" value={sel.valor_locacao || ''} onChange={e => fatUpdateValor(op.id, 'valor_locacao', e.target.value)} placeholder="0,00" className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-400">R$</span>
                                <input type="number" step="0.01" min="0" value={sel.valor_ctr || ''} onChange={e => fatUpdateValor(op.id, 'valor_ctr', e.target.value)} placeholder="0,00" className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-50 border-t px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="text-center"><p className="text-xl font-bold text-gray-800">{fatTotals.count}</p><p className="text-xs text-gray-500">Selecionadas</p></div>
                      <div className="h-8 w-px bg-gray-300"></div>
                      <div className="text-center"><p className="text-xl font-bold text-blue-600">{formatMoney(fatTotals.locacao)}</p><p className="text-xs text-gray-500">Total Locacao</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-teal-600">{formatMoney(fatTotals.ctr)}</p><p className="text-xs text-gray-500">Total CTR</p></div>
                      <div className="h-8 w-px bg-gray-300"></div>
                      <div className="text-center"><p className="text-xl font-bold text-green-600">{formatMoney(fatTotals.locacao + fatTotals.ctr)}</p><p className="text-xs text-gray-500">Total Geral</p></div>
                    </div>
                    <button onClick={handleGerarMedicao} disabled={fatTotals.count === 0 || fatGerando} className="flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                      {fatGerando ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                      {fatGerando ? 'Gerando...' : 'Gerar Medicao'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {fatClienteId && !fatBuscando && fatOperacoes.length === 0 && <EmptyState icon={DollarSign} message="Nenhuma operacao de recolha encontrada para os filtros selecionados" />}
            {!fatClienteId && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4"><DollarSign size={32} className="text-blue-500" /></div>
                <h3 className="font-bold text-gray-700 text-lg">Selecione um cliente</h3>
                <p className="text-gray-400 mt-1">Escolha um cliente e periodo para buscar as operacoes de recolha</p>
              </div>
            )}
          </>
        )}
        {medicaoTab === 'historico' && (
          <div>
            <div className="bg-white border rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2"><FileText size={16} /> Medicoes Geradas</h3>
                <button onClick={loadMedicoesHistorico} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><RefreshCw size={14} /> Atualizar</button>
              </div>
            </div>
            {medicoesHistorico.length === 0 ? <EmptyState icon={FileText} message="Nenhuma medicao gerada ainda" /> : (
              <div className="space-y-3">
                {medicoesHistorico.map(med => (
                  <div key={med.id} className="bg-white border rounded-xl p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-gray-800">Medicao #{med.id}</h3>
                          <Badge text="Faturado" color="green" />
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1"><Users size={13} /> {med.cliente_nome || 'Cliente #' + med.cliente_id}</span>
                          <span className="flex items-center gap-1"><Calendar size={13} /> {med.data_criacao ? new Date(med.data_criacao).toLocaleDateString('pt-BR') : '-'}</span>
                          <span className="flex items-center gap-1"><DollarSign size={13} /> {formatMoney(med.valor_total)}</span>
                          {med.qtd_operacoes && <span className="text-xs text-gray-400">{med.qtd_operacoes} operacao(oes)</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        <button onClick={() => handleEditMedicao(med)} title="Ver Detalhes" className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-xs font-semibold"><Eye size={14} /> Detalhes</button>
                        <button onClick={() => handleGerarPDFMedicao(med.id)} title="Baixar PDF" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"><Download size={16} /></button>
                        <button onClick={() => handleDeleteMedicao(med.id)} title="Excluir" className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const RelatoriosPage = () => {
    const ctrSel = ctrs.find(c => c.id === parseInt(relCtrId));
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Relatorios</h1>
        <div className="bg-white border rounded-xl p-5 mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2"><BarChart2 size={16} /> Relatorio de Uso por CTR</h3>
          <p className="text-sm text-gray-500 mb-4">Selecione um CTR e o periodo para visualizar os descartes realizados e gerar o PDF do relatorio.</p>
          <div className="grid grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTR *</label>
              <select value={relCtrId} onChange={e => { setRelCtrId(e.target.value); setRelResultado(null); }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white">
                <option value="">Selecione o CTR...</option>
                {ctrs.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicio *</label>
              <input type="date" value={relDataInicio} onChange={e => setRelDataInicio(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim *</label>
              <input type="date" value={relDataFim} onChange={e => setRelDataFim(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
            </div>
            <button onClick={buscarRelatorioCtr} disabled={relBuscando || !relCtrId || !relDataInicio || !relDataFim} className="bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed h-10">
              {relBuscando ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
              {relBuscando ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
        {relResultado && (
          <>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-teal-800 text-lg">{ctrSel?.nome || relResultado.ctr_nome || 'CTR'}</h3>
                  <p className="text-sm text-teal-600 flex items-center gap-1 mt-1"><MapPin size={13} /> {ctrSel?.endereco || relResultado.ctr_endereco || '-'}</p>
                  {(ctrSel?.cnpj || relResultado.ctr_cnpj) && <p className="text-sm text-teal-600 flex items-center gap-1 mt-0.5"><Hash size={13} /> CNPJ: {ctrSel?.cnpj || relResultado.ctr_cnpj}</p>}
                  {(ctrSel?.licenca_ambiental || relResultado.ctr_licenca) && <p className="text-sm text-teal-600 flex items-center gap-1 mt-0.5"><FileText size={13} /> Licenca: {ctrSel?.licenca_ambiental || relResultado.ctr_licenca}</p>}
                  <p className="text-xs text-teal-500 mt-2">Periodo: {new Date(relDataInicio + 'T00:00:00').toLocaleDateString('pt-BR')} ate {new Date(relDataFim + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="text-center bg-white rounded-xl px-8 py-4 border border-teal-200 shadow-sm">
                  <p className="text-4xl font-bold text-teal-700">{relResultado.total_descartes ?? relResultado.operacoes?.length ?? 0}</p>
                  <p className="text-xs text-teal-500 mt-1">descartes no periodo</p>
                </div>
              </div>
            </div>
            {relResultado.operacoes && relResultado.operacoes.length > 0 ? (
              <div className="bg-white border rounded-xl overflow-hidden mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Rota</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Cacamba</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Motorista</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Veiculo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relResultado.operacoes.map((op, i) => (
                        <tr key={op.id || i} className="border-b last:border-0 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-sm text-gray-400 font-medium">{i + 1}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{op.data_hora ? new Date(op.data_hora).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{op.rota_nome || `Rota #${op.rota_id || '-'}`}</td>
                          <td className="px-4 py-3"><span className="text-sm font-medium text-gray-800">#{op.cacamba_numero || '-'}</span>{op.cacamba_tamanho && <span className="text-xs text-gray-500 ml-1">({op.cacamba_tamanho})</span>}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{op.cliente_nome || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{op.motorista_nome || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{op.veiculo_placa || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-50 border-t px-5 py-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600 font-medium">Total: <strong>{relResultado.operacoes.length}</strong> descarte(s) no periodo selecionado</p>
                  <button onClick={gerarPdfRelatorioCtr} className="flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-700 transition text-sm"><Download size={16} /> Gerar Relatorio PDF</button>
                </div>
              </div>
            ) : <EmptyState icon={FileText} message="Nenhum descarte encontrado neste periodo para o CTR selecionado" />}
          </>
        )}
        {!relCtrId && !relResultado && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4"><BarChart2 size={32} className="text-teal-500" /></div>
            <h3 className="font-bold text-gray-700 text-lg">Relatorio de Uso por CTR</h3>
            <p className="text-gray-400 mt-1">Selecione um CTR e o periodo desejado para gerar o relatorio de descartes</p>
          </div>
        )}
        {relCtrId && !relResultado && !relBuscando && (
          <div className="text-center py-12 text-gray-400"><Search size={36} className="mx-auto mb-3 opacity-50" /><p>Preencha as datas e clique em "Buscar" para gerar o relatorio</p></div>
        )}
      </div>
    );
  };

  const AlertasPage = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Alertas - Cacambas Paradas</h1>
        <button onClick={loadParadas} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2 text-sm"><RefreshCw size={16} /> Atualizar</button>
      </div>
      {paradas.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Package size={32} className="text-green-500" /></div>
          <h3 className="font-bold text-gray-700 text-lg">Tudo certo!</h3>
          <p className="text-gray-400 mt-1">Nenhuma cacamba parada ha mais de 3 dias</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paradas.map(p => (
            <div key={p.id} className="bg-white border-2 border-red-100 rounded-xl p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center font-bold text-red-600">#{p.numero}</div>
                  <div>
                    <h3 className="font-bold text-gray-800">Cacamba #{p.numero} <span className="font-normal text-sm text-gray-500">({p.tamanho})</span></h3>
                    {p.cliente_nome && <p className="text-sm text-gray-600 mt-0.5">Cliente: <strong>{p.cliente_nome}</strong></p>}
                    {p.cliente_endereco && <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} /> {p.cliente_endereco}</p>}
                    {p.cliente_telefone && <p className="text-sm text-gray-500 flex items-center gap-1"><Phone size={12} /> {p.cliente_telefone}</p>}
                    <p className="text-xs text-gray-400 mt-1">Ultima movimentacao: {p.data_ultima_movimentacao ? new Date(p.data_ultima_movimentacao).toLocaleDateString('pt-BR') : 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-center">
                  <p className="text-2xl font-bold">{p.dias_parada}</p>
                  <p className="text-xs">dias</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return DashboardPage();
      case 'clientes': return ClientesPage();
      case 'motoristas': return MotoristasPage();
      case 'veiculos': return VeiculosPage();
      case 'cacambas': return CacambasPage();
      case 'ctrs': return CtrsPage();
      case 'rotas': return RotasPage();
      case 'medicao': return MedicaoPage();
      case 'relatorios': return RelatoriosPage();
      case 'alertas': return AlertasPage();
      default: return DashboardPage();
    }
  };

  // ============================
  // ========== RENDER ==========
  // ============================

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <aside className={`${sideOpen ? 'w-56' : 'w-16'} bg-gray-900 text-white flex flex-col transition-all duration-300 flex-shrink-0`}>
        <div className="p-4 flex items-center gap-3">
          <button onClick={() => setSideOpen(!sideOpen)} className="p-1 hover:bg-gray-700 rounded"><Menu size={20} /></button>
          {sideOpen && <span className="font-bold text-sm">Elo Gestao de Residuos</span>}
        </div>
        <nav className="flex-1 mt-2">
          {navItems.map(item => (
            <button key={item.id} onClick={() => setPage(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${page === item.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}>
              <item.icon size={18} />
              {sideOpen && <span>{item.label}</span>}
              {item.id === 'alertas' && paradas.length > 0 && sideOpen && <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{paradas.length}</span>}
            </button>
          ))}
        </nav>
        <div className="border-t border-gray-700">
          {sideOpen && (
            <div className="px-4 py-3">
              <p className="text-xs text-gray-400 flex items-center gap-1"><User size={12} /> {currentUser}</p>
            </div>
          )}
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-900 hover:text-red-300 transition">
            <LogOut size={18} />
            {sideOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Home size={14} /><span>/</span>
            <span className="text-gray-800 font-medium">{pageDisplayNames[page] || page}</span>
          </div>
          <button onClick={loadAll} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} />Atualizar</button>
        </header>
        <div className="p-6 max-w-5xl mx-auto">{renderPage()}</div>
      </main>

      {modal === 'cliente' && <ClienteForm />}
      {modal === 'motorista' && <MotoristaForm />}
      {modal === 'veiculo' && <VeiculoForm />}
      {modal === 'cacamba' && <CacambaForm />}
      {modal === 'ctr' && <CtrForm />}
      {modal === 'rota' && (
        <RotaFormComponent
          clientes={clientes}
          motoristas={motoristas}
          veiculos={veiculos}
          cacambas={cacambas}
          ctrs={ctrs}
          setQuickAdd={setQuickAdd}
          onClose={() => setModal(null)}
          onSubmit={async (payload, erroLocal) => {
            if (erroLocal) { showToast(erroLocal, 'error'); return; }
            await handleCreate('rotas', payload, 'Rota');
          }}
        />
      )}
      {showEditarRota && editandoRotaData && <EditRotaForm />}

      {showDetalhes && rotaSelecionada && (
        <FullModal title={rotaSelecionada.nome || `Rota #${rotaSelecionada.id}`} onClose={() => setShowDetalhes(false)}
          actions={<button onClick={() => handleGerarPDF(rotaSelecionada.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"><Download size={14} /> Baixar PDF</button>}>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4 border"><div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Clock size={14} /> Data/Hora</div><p className="font-semibold text-gray-800 text-sm">{formatarData(rotaSelecionada.data_hora)}</p></div>
            <div className="bg-gray-50 rounded-xl p-4 border"><div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><User size={14} /> Motorista</div><p className="font-semibold text-gray-800 text-sm">{rotaSelecionada.motorista_nome || '-'}</p></div>
            <div className="bg-gray-50 rounded-xl p-4 border"><div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Car size={14} /> Veiculo</div><p className="font-semibold text-gray-800 text-sm">{rotaSelecionada.veiculo_placa ? `${rotaSelecionada.veiculo_placa} - ${rotaSelecionada.veiculo_modelo}` : '-'}</p></div>
          </div>
          <div className="flex items-center gap-2 mb-1"><span className="text-sm font-medium text-gray-500">Status:</span>{rotaStatusBadge(rotaSelecionada.status)}</div>
          {rotaSelecionada.observacoes && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 mt-3"><p className="text-sm text-amber-800"><strong>Obs:</strong> {rotaSelecionada.observacoes}</p></div>}
          <h3 className="font-bold text-gray-700 mb-3 mt-4">Operacoes ({rotaSelecionada.operacoes?.length || 0})</h3>
          {(rotaSelecionada.operacoes || []).map((op, i) => {
            const tb = tipoBadge(op.tipo);
            return (
              <div key={op.id} className={`border-2 rounded-xl p-4 mb-3 ${op.status === 'concluida' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${op.status === 'concluida' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{op.status === 'concluida' ? <Check size={14} /> : i + 1}</span>
                    <Badge text={tb.text} color={tb.color} />
                    <OpLabel op={op} />
                    <ArrowRight size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-600">{op.cliente_nome || '-'}</span>
                  </div>
                  <span className={`text-xs font-semibold ${op.status === 'concluida' ? 'text-green-600' : 'text-amber-500'}`}>{op.status === 'concluida' ? 'Concluida' : 'Pendente'}</span>
                </div>
                {op.tipo === 'recolher_entregar' && op.cliente_entregar_nome && <p className="text-xs text-orange-600 mt-2 ml-10 flex items-center gap-1">Entrega para: <strong>{op.cliente_entregar_nome}</strong></p>}
                {tipoPrecisaCtr(op.tipo) && op.ctr_nome && <p className="text-xs text-teal-600 mt-2 ml-10 flex items-center gap-1"><FileText size={12} /> CTR: {op.ctr_nome}</p>}
                {op.observacoes && <p className="text-xs text-gray-500 mt-2 ml-10">Obs: {op.observacoes}</p>}
                {op.fotos && op.fotos.length > 0 && (
                  <div className="mt-3 ml-10">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Fotos ({op.fotos.length})</p>
                    <div className="flex gap-2 flex-wrap">
                      {op.fotos.map(foto => (
                        <img key={foto.id} src={foto.caminho_arquivo} alt="Evidencia" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition" onClick={() => window.open(foto.caminho_arquivo, '_blank')} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {rotaSelecionada.status === 'concluida' && rotaSelecionada.data_finalizacao && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
              <h4 className="font-bold text-green-700 text-sm mb-1">Rota Finalizada</h4>
              <p className="text-xs text-gray-600">Em {formatarData(rotaSelecionada.data_finalizacao)}</p>
              {rotaSelecionada.observacoes_finalizacao && <p className="text-xs text-gray-600 mt-1">Obs: {rotaSelecionada.observacoes_finalizacao}</p>}
            </div>
          )}
        </FullModal>
      )}

      {showFinalizar && rotaSelecionada && (
        <FullModal title={`Finalizar: ${rotaSelecionada.nome || 'Rota #' + rotaSelecionada.id}`} onClose={() => !finalizando && setShowFinalizar(false)}>
          <p className="text-sm text-gray-500 mb-4">Confirme cada operacao, adicione fotos e selecione o CTR para recolhas/trocas/rec+ent.</p>
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-800"><strong>Obrigatorio:</strong> Cada operacao precisa de pelo menos uma foto. Operacoes de recolha, troca e rec+ent precisam de um CTR selecionado.</span>
          </div>
          {finalizacaoData.operacoes.map((op, opIndex) => {
            const tb = tipoBadge(op.tipo);
            return (
              <div key={op.id} className={`border-2 rounded-xl p-4 mb-4 ${op.concluida ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${op.concluida ? 'bg-green-500 text-white' : 'bg-amber-400 text-white'}`}>{op.concluida ? <Check size={14} /> : opIndex + 1}</span>
                    <Badge text={tb.text} color={tb.color} />
                    <OpLabel op={op} />
                    <ArrowRight size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-600">{op.cliente_nome || '-'}</span>
                    {op.tipo === 'recolher_entregar' && op.cliente_entregar_nome && <span className="text-sm text-orange-600 font-medium">{'\u2192'} Entrega: {op.cliente_entregar_nome}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleConcluida(opIndex)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${op.concluida ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                      {op.concluida ? <><Check size={14} /> Concluida</> : 'Marcar Concluida'}
                    </button>
                    {!op.concluida && (
                      <button
                        onClick={() => removerOperacaoDaRota(opIndex)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition"
                        title="Remover esta operacao da rota"
                      >
                        <Trash2 size={13} /> Remover
                      </button>
                    )}
                  </div>
                </div>
                {tipoPrecisaCtr(op.tipo) && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">CTR - Local de Descarte *</label>
                    <select value={op.ctr_id} onChange={(e) => { const ops = [...finalizacaoData.operacoes]; ops[opIndex].ctr_id = e.target.value; setFinalizacaoData({ ...finalizacaoData, operacoes: ops }); }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white">
                      <option value="">Selecione o CTR...</option>
                      {ctrs.map(c => <option key={c.id} value={c.id}>{c.nome} - {c.endereco}</option>)}
                    </select>
                    {!op.ctr_id && <p className="text-xs text-red-500 mt-1">Obrigatorio selecionar CTR</p>}
                    {ctrs.length === 0 && <p className="text-xs text-amber-600 mt-1">Nenhum CTR cadastrado. Cadastre em CTRs no menu lateral.</p>}
                  </div>
                )}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Observacoes desta operacao</label>
                  <input type="text" value={op.observacoes_op} onChange={(e) => { const ops = [...finalizacaoData.operacoes]; ops[opIndex].observacoes_op = e.target.value; setFinalizacaoData({ ...finalizacaoData, operacoes: ops }); }} placeholder="Alguma observacao..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Fotos de Evidencia *</label>
                  <div className="flex gap-2 flex-wrap">
                    {(op.fotos_existentes || []).map(foto => (
                      <div key={foto.id} className="relative">
                        <img src={foto.caminho_arquivo} alt="Evidencia" className="w-20 h-20 object-cover rounded-lg border-2 border-green-400" />
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs"><Check size={10} /></span>
                      </div>
                    ))}
                    {(op.fotos_novas || []).map((foto, fotoIndex) => (
                      <div key={fotoIndex} className="relative">
                        <img src={foto.preview} alt="Nova foto" className="w-20 h-20 object-cover rounded-lg border-2 border-blue-400" />
                        <button onClick={() => removerFotoNova(opIndex, fotoIndex)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X size={10} /></button>
                      </div>
                    ))}
                    <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                      <Camera size={18} className="text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">Adicionar</span>
                      <input type="file" accept="image/*" multiple onChange={(e) => handleFotoChange(opIndex, e)} className="hidden" />
                    </label>
                  </div>
                  {(op.fotos_existentes?.length || 0) === 0 && (op.fotos_novas?.length || 0) === 0 && <p className="text-xs text-red-500 mt-1">Obrigatorio pelo menos uma foto</p>}
                </div>
              </div>
            );
          })}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observacoes da Finalizacao</label>
            <textarea value={finalizacaoData.observacoes_finalizacao} onChange={(e) => setFinalizacaoData({ ...finalizacaoData, observacoes_finalizacao: e.target.value })} rows={3} placeholder="Observacoes gerais sobre a finalizacao..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>
          <div className="grid grid-cols-4 gap-3 bg-gray-50 rounded-xl p-4 border mb-6">
            <div className="text-center"><p className="text-2xl font-bold text-gray-800">{finalizacaoData.operacoes.length}</p><p className="text-xs text-gray-500">Total</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-green-600">{finalizacaoData.operacoes.filter(op => op.concluida).length}</p><p className="text-xs text-gray-500">Concluidas</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-blue-600">{finalizacaoData.operacoes.reduce((acc, op) => acc + (op.fotos_existentes?.length || 0) + (op.fotos_novas?.length || 0), 0)}</p><p className="text-xs text-gray-500">Fotos</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-red-500">{finalizacaoData.operacoes.filter(op => tipoPrecisaCtr(op.tipo) && !op.ctr_id).length}</p><p className="text-xs text-gray-500">Sem CTR</p></div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => !finalizando && setShowFinalizar(false)} disabled={finalizando} className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 text-sm">Cancelar</button>
            <button onClick={() => handleGerarPDF(rotaSelecionada.id)} className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 text-sm"><Download size={16} /> Baixar PDF</button>
            <button onClick={handleFinalizarRota} disabled={finalizando} className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 text-sm disabled:opacity-50">
              <CheckCircle size={16} /> {finalizando ? 'Finalizando...' : 'Finalizar Rota'}
            </button>
          </div>
        </FullModal>
      )}

      {editandoMedicao && (
        <FullModal title={`Medicao #${editandoMedicao.id}`} onClose={() => setEditandoMedicao(null)}>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Check size={16} className="text-green-600" />
            <span className="text-sm text-green-800 font-medium">Esta medicao ja foi faturada{editandoMedicao.data_faturamento && ` em ${new Date(editandoMedicao.data_faturamento).toLocaleDateString('pt-BR')}`}</span>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border mb-4">
            <div className="grid grid-cols-3 gap-4">
              <div><p className="text-xs text-gray-500">Cliente</p><p className="font-semibold text-gray-800">{editandoMedicao.cliente_nome || '-'}</p></div>
              <div><p className="text-xs text-gray-500">Data</p><p className="font-semibold text-gray-800">{editandoMedicao.data_criacao ? new Date(editandoMedicao.data_criacao).toLocaleDateString('pt-BR') : '-'}</p></div>
              <div><p className="text-xs text-gray-500">Valor Total</p><p className="font-semibold text-green-700">{formatMoney(editandoMedicao.valor_total)}</p></div>
            </div>
          </div>
          {editandoMedicao.operacoes && editandoMedicao.operacoes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">Data</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">Cacamba</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">Rota</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500">Valor Locacao</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500">Valor CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {editandoMedicao.operacoes.map((op, i) => (
                    <tr key={op.id || i} className="border-b last:border-0">
                      <td className="px-3 py-2 text-gray-700">{op.data_hora ? new Date(op.data_hora).toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="px-3 py-2 text-gray-700">#{op.cacamba_numero || '-'} {op.cacamba_tamanho ? `(${op.cacamba_tamanho})` : ''}</td>
                      <td className="px-3 py-2 text-gray-600">{op.rota_nome || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatMoney(op.valor_locacao)}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatMoney(op.valor_ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FileText size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Detalhes das operacoes nao disponiveis</p>
              <p className="text-xs mt-1">Valor total: {formatMoney(editandoMedicao.valor_total)}</p>
              {editandoMedicao.qtd_operacoes && <p className="text-xs">{editandoMedicao.qtd_operacoes} operacao(oes)</p>}
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditandoMedicao(null)} className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 text-sm">Fechar</button>
            <button onClick={() => handleGerarPDFMedicao(editandoMedicao.id)} className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 text-sm"><Download size={16} /> Baixar PDF</button>
          </div>
        </FullModal>
      )}

      {quickAdd && quickAdd.tipo === 'cliente' && (
        <QuickAddClienteModal
          onClose={() => setQuickAdd(null)}
          showToast={showToast}
          onCreated={async (novoId) => {
            await load('clientes');
            if (quickAdd && quickAdd.onCreated) quickAdd.onCreated(novoId);
          }}
        />
      )}
      {quickAdd && quickAdd.tipo === 'cacamba' && (
        <QuickAddCacambaModal
          onClose={() => setQuickAdd(null)}
          showToast={showToast}
          contexto={quickAdd.contexto}
          clientes={clientes}
          onCreated={async (novoId) => {
            await load('cacambas');
            await loadParadas();
            if (quickAdd && quickAdd.onCreated) quickAdd.onCreated(novoId);
          }}
        />
      )}

      {conflitoConclusao && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80] p-4" onClick={conflitoConclusao.onCancelar}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center gap-3">
              <AlertTriangle size={20} className="text-amber-500 flex-shrink-0" />
              <h2 className="font-bold text-gray-800">Operação fora de ordem cronológica</h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-4">{conflitoConclusao.message}</p>
              {conflitoConclusao.op_conflito && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
                  <p className="font-bold mb-1">Operação anterior pendente:</p>
                  <p>Tipo: <strong>{conflitoConclusao.op_conflito.tipo}</strong></p>
                  <p>Rota: <strong>{conflitoConclusao.op_conflito.rota_nome}</strong></p>
                  <p>Data: <strong>{conflitoConclusao.op_conflito.rota_data ? new Date(conflitoConclusao.op_conflito.rota_data).toLocaleString('pt-BR') : '-'}</strong></p>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5 text-xs text-blue-800">
                <strong>Recomendação:</strong> cancele esta finalização, abra a rota anterior e conclua a operação dela primeiro. Isso garante que o estado da caçamba fique correto.
              </div>
              <div className="flex gap-3">
                <button
                  onClick={conflitoConclusao.onCancelar}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm"
                >
                  Cancelar e resolver primeiro
                </button>
                <button
                  onClick={conflitoConclusao.onConfirmar}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 text-sm border"
                >
                  Concluir mesmo assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
